// CORNER_V2: Contract velocity run wizard.
//
// Replaces the single-form HUD for workflows whose graph contains a
// dispatch_for_signature node. Walks the user through three input screens
// (Recipients → Document Source → Template Preview) and finally hands off to
// POST /api/workflow-runs, after which the existing 4-stage SSE progress
// strip plays. Action-node side of the engine (clone/fill/sign/dispatch)
// already exists — this file only owns the input phase + the run handoff.

import {
  type CSSProperties,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PdfSigningViewer from '../../pdfViewer/PdfSigningViewer';
import type { ViewerSigner } from '../../pdfViewer/signingTypes';
import axios from 'axios';
import type {
  CapturedPlaceholderValue,
  DocEditRecoveryResult,
  DispatchForSignatureNodeConfig,
  DispatchRecipientShape,
  DocumentProjectDetail,
  DocumentVersionSummary,
  EditBatch,
  FillPlaceholdersNodeConfig,
  SignatureField,
  ToolResult,
  WorkflowDefinition,
} from '@corner/shared';
import { APP } from '../tokens';
import { useAuthContext } from '../../../contexts/AuthContext';
import { isRecipientPlacementField } from '../../../lib/isRecipientPlacementField';
import { useRecentRecipients } from '../../../hooks/useRecentRecipients';
import {
  getContractsKnowledgeBase,
  listRecentContracts,
  listRecentDocuments,
  updateUserWorkflow,
  type ContractsKnowledgeBase,
  type RecentContract,
  type RecentDocument,
} from '../../../api/workflowApi';
import * as projectApi from '../../../api/projectApi';
import { removeStep, reorderSteps } from '../../../lib/workflowGraphOps';
import { SaveAsWorkflowPrompt, type SaveAsWorkflowMode } from './SaveAsWorkflowPrompt';
import {
  type WorkflowRunStep,
  type WorkflowRunStatus,
  useWorkflowRunStream,
} from '../../../hooks/useWorkflowRunStream';
import { buildWorkflowThreadSummary } from '../../../lib/buildWorkflowThreadSummary';
import type { WorkflowThreadSummary } from '../../../types';
import CoTEditBatchCard from '../cot/CoTEditBatchCard';
import { AppFolderIcon } from '../AppFolderIcon';
import { toast } from '../../../hooks/use-toast';
import PaginatedDocument from '../PaginatedDocument';
import { paragraphsToSimpleDraftHtml } from '../../../lib/draftEditSync';

/** Clone preset marks runtime-selected docs — never fetch `/api/file/__runtime__`. */
const WORKFLOW_RUNTIME_SOURCE_PLACEHOLDER = '__runtime__';
const DOC_WIZARD_EDIT_DEBUG =
  import.meta.env.DEV ||
  import.meta.env.VITE_DOC_WIZARD_EDIT_DEBUG === '1' ||
  import.meta.env.DOC_WIZARD_EDIT_DEBUG === '1';

function wizardEditDebugLog(event: string, payload: Record<string, unknown>): void {
  if (!DOC_WIZARD_EDIT_DEBUG) return;
  console.log(`[WizardEdit] ${event}`, payload);
}

function isResolvedWorkflowSourceFileId(fileId: string | null | undefined): fileId is string {
  return typeof fileId === 'string'
    && fileId.trim().length > 0
    && fileId !== WORKFLOW_RUNTIME_SOURCE_PLACEHOLDER;
}

function isPdfDocumentVersion(version?: DocumentVersionSummary | null): boolean {
  const mime = (version?.mimeType ?? '').toLowerCase();
  if (mime) return mime === 'application/pdf' || mime.includes('/pdf');
  return /\.pdf$/i.test(version?.fileName ?? '');
}

function isDocxDocumentVersion(version?: DocumentVersionSummary | null): boolean {
  const mime = (version?.mimeType ?? '').toLowerCase();
  return (
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mime === 'application/msword' ||
    /\.(docx|doc)$/i.test(version?.fileName ?? '')
  );
}

// ─── Public props ──────────────────────────────────────────────────────────

export interface ContractRunWizardProps {
  /** Source workflow definition (must contain a dispatch_for_signature node). */
  definition: WorkflowDefinition;
  /** UserWorkflow id used as the `workflowId` in the run POST. */
  workflowId: string;
  /** Token getter for SSE/auth. */
  getToken: () => string | null;
  /** Active run id once a run has been kicked off (null until the user clicks Send). */
  runId: string | null;
  /** Called when the user submits the wizard. */
  onRun: (params: {
    workflowId: string;
    initialVariables: Record<string, unknown>;
    documentIds: string[];
  }) => void | Promise<void>;
  /** Optional close handler. */
  onClose?: () => void;
  /** Optional run-again handler shown after a terminal run. */
  onRunAgain?: () => void;
  /** Optional failure recovery handler that clears the run and returns to review. */
  onGoBack?: () => void;

  // ── Template memory (UI-first; backend lands in vertical 3) ─────
  /** Saved templates for the parent workflow. Surfaced as the gold-path
   *  section in the document-source picker. Empty array hides the
   *  section. */
  templates?: WorkflowTemplateSummary[];
  /** When set, controls which "save" prompt appears after a successful
   *  send. Omit to hide the prompt entirely (e.g. running an established
   *  workflow with the user's preferred templates already saved). */
  saveAsMode?: SaveAsWorkflowMode;
  /** For mode='template' display copy. Ignored otherwise. */
  saveAsWorkflowName?: string;
  /** Default value pre-filled in the name input. */
  saveAsDefaultName?: string;
  /**
   * Called when the user confirms the save prompt. Parent owns the
   * actual API call (saveContractSendAsWorkflow for mode='workflow',
   * createTemplate for mode='template'). The wizard exposes the
   * signingRequestId via `signingRequestId` arg so the parent can
   * forward it to the existing /api/workflows/from-contract-send endpoint.
   */
  onSaveAsWorkflow?: (input: {
    name: string;
    mode: SaveAsWorkflowMode;
    signingRequestId?: string;
    /** The ProcessedFile.fileId of the source doc this run used.
     *  Forwarded so the parent can create a TemplateDocument under
     *  this workflow with that file as the master. */
    sourceFileId?: string;
  }) => void | Promise<void>;
  /** Posted once per completed run — parent appends a chat summary strip. */
  onAppendThreadSummary?: (summary: WorkflowThreadSummary) => void;
}

// ─── Local types ───────────────────────────────────────────────────────────

interface RecipientDraft {
  to: { email: string; name: string };
  cc: string[];
  subject: string;
  message: string;
}

type WizardStep = 'recipients' | 'source' | 'preview';

// Roles that count as "the recipient" — Screen 1's To name/email automatically
// fills any placeholder whose role matches one of these.
const RECIPIENT_ROLE_PATTERNS = [
  /^buyer_name$/i,
  /^client_name$/i,
  /^recipient_name$/i,
  /^signer_name$/i,
  /^party_name$/i,
];
const RECIPIENT_EMAIL_ROLE_PATTERNS = [
  /^buyer_email$/i,
  /^client_email$/i,
  /^recipient_email$/i,
  /^signer_email$/i,
];

function isEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

interface DerivedDelta {
  key: string;
  label: string;
  hint?: string;
  required: boolean;
}

interface DerivedGraph {
  recipients: DispatchRecipientShape[];
  fillConfig: FillPlaceholdersNodeConfig | null;
  dispatchConfig: DispatchForSignatureNodeConfig | null;
  defaultSourceFileId: string | null;
  /** Placeholder deltas (excluding recipient slots). */
  extraDeltas: DerivedDelta[];
}

function deriveFromDefinition(definition: WorkflowDefinition): DerivedGraph {
  const cloneNode = definition.graph.nodes.find((n) => n.type === 'clone_document');
  const fillNode = definition.graph.nodes.find((n) => n.type === 'fill_placeholders');
  const dispatchNode = definition.graph.nodes.find((n) => n.type === 'dispatch_for_signature');

  const recipients: DispatchRecipientShape[] =
    dispatchNode?.type === 'dispatch_for_signature' ? dispatchNode.config.recipients : [];
  const fillConfig: FillPlaceholdersNodeConfig | null =
    fillNode?.type === 'fill_placeholders' ? fillNode.config : null;
  const dispatchConfig: DispatchForSignatureNodeConfig | null =
    dispatchNode?.type === 'dispatch_for_signature' ? dispatchNode.config : null;
  const rawCloneSource =
    cloneNode?.type === 'clone_document' ? cloneNode.config.sourceFileId : null;
  const defaultSourceFileId =
    rawCloneSource && rawCloneSource !== WORKFLOW_RUNTIME_SOURCE_PLACEHOLDER
      ? rawCloneSource
      : null;

  // Placeholder deltas (excluding recipient slots already collected on Screen 1).
  const recipientKeys = new Set<string>();
  for (const r of recipients) {
    recipientKeys.add(r.emailKey);
    recipientKeys.add(r.nameKey);
  }

  const extraDeltas: DerivedDelta[] = [];
  if (fillConfig) {
    const baselineByKey = new Map<string, CapturedPlaceholderValue>();
    for (const v of fillConfig.baselineValues) {
      if (v.deltaKey) baselineByKey.set(v.deltaKey, v);
    }
    for (const dKey of fillConfig.deltaKeys) {
      if (recipientKeys.has(dKey)) continue;
      const baseline = baselineByKey.get(dKey);
      extraDeltas.push({
        key: dKey,
        label: baseline?.label ?? dKey,
        hint: baseline?.value ? `Last time: ${baseline.value}` : undefined,
        required: false,
      });
    }
  }

  return { recipients, fillConfig, dispatchConfig, defaultSourceFileId, extraDeltas };
}

/**
 * Apply the recipient name/email captured on Screen 1 to any baseline value
 * whose role matches the recipient role patterns. Used both in the preview
 * (so the user sees the correct name) and in the final initialVariables map
 * so the server-side fill matches what was previewed.
 */
function applyRecipientToBaseline(
  baseline: CapturedPlaceholderValue[],
  recipientName: string,
  recipientEmail: string,
): Map<string, string> {
  const overrides = new Map<string, string>();
  for (const v of baseline) {
    const role = v.role ?? '';
    if (recipientName && RECIPIENT_ROLE_PATTERNS.some((re) => re.test(role))) {
      overrides.set(v.fieldId, recipientName);
    } else if (recipientEmail && RECIPIENT_EMAIL_ROLE_PATTERNS.some((re) => re.test(role))) {
      overrides.set(v.fieldId, recipientEmail);
    }
  }
  return overrides;
}

// ─── Main component ────────────────────────────────────────────────────────

export default function ContractRunWizard({
  definition,
  workflowId,
  getToken,
  runId,
  onRun,
  onClose,
  onRunAgain,
  onGoBack,
  templates,
  saveAsMode,
  saveAsWorkflowName,
  saveAsDefaultName,
  onSaveAsWorkflow,
  onAppendThreadSummary,
}: ContractRunWizardProps) {
  const auth = useAuthContext();
  // Local working copy of the definition so reorder + delete actions can
  // update the UI optimistically. Synced from the prop on prop change so
  // external updates (parent refetches the workflow) flow through.
  const [workingDef, setWorkingDef] = useState<WorkflowDefinition>(definition);
  useEffect(() => { setWorkingDef(definition); }, [definition]);

  const derived = useMemo(() => deriveFromDefinition(workingDef), [workingDef]);
  const primaryRecipient: DispatchRecipientShape | null = derived.recipients[0] ?? null;

  // ── Per-screen state ────────────────────────────────────────────
  const [step, setStep] = useState<WizardStep>('recipients');
  const [recipient, setRecipient] = useState<RecipientDraft>({
    to: { email: '', name: '' },
    cc: [],
    subject: '',
    message: '',
  });
  const [chosenSourceFileId, setChosenSourceFileId] = useState<string | null>(
    derived.defaultSourceFileId,
  );
  const [chosenSourceLabel, setChosenSourceLabel] = useState<string | null>(null);
  const [extraDeltaValues, setExtraDeltaValues] = useState<Record<string, string>>({});
  const [documentProject, setDocumentProject] = useState<DocumentProjectDetail | null>(null);
  const [activeVersionId, setActiveVersionId] = useState<string | null>(null);
  const pendingWizardApplyFileRef = useRef<{
    fileId: string;
    previousFileId: string | null;
    batchId: string;
    createdAt: number;
    warned: boolean;
  } | null>(null);
  const [markerFieldsByVersion, setMarkerFieldsByVersion] = useState<Record<string, SignatureField[]>>({});
  const [editableDraftHtmlByVersion, setEditableDraftHtmlByVersion] = useState<Record<string, string>>({});
  const [editBatch, setEditBatch] = useState<EditBatch | null>(null);
  const [aiEditPrompt, setAiEditPrompt] = useState('');
  const [aiEditing, setAiEditing] = useState(false);
  const [aiEditError, setAiEditError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [applyingWizardMarks, setApplyingWizardMarks] = useState(false);
  const [flowPanelOpen, setFlowPanelOpen] = useState(false);
  const runIdPreviousRef = useRef<string | null>(null);
  const preservePreviewOnRunClearRef = useRef(false);

  const fallbackSourceVersion = useMemo<DocumentVersionSummary | null>(() => {
    if (!chosenSourceFileId) return null;
    return {
      id: `file:${chosenSourceFileId}`,
      projectId: `file:${chosenSourceFileId}`,
      versionNumber: 1,
      fileId: chosenSourceFileId,
      fileName: chosenSourceLabel ?? 'Selected document',
      mimeType: 'application/pdf',
      source: 'original',
      lineageKind: 'version',
      createdBy: 'local',
      createdFromVersionId: null,
      changeSummary: 'Selected source document.',
      createdAt: new Date(0).toISOString(),
    };
  }, [chosenSourceFileId, chosenSourceLabel]);
  const documentVersions = useMemo<DocumentVersionSummary[]>(() => (
    documentProject?.versions?.length ? documentProject.versions : fallbackSourceVersion ? [fallbackSourceVersion] : []
  ), [documentProject, fallbackSourceVersion]);
  const activeVersion = useMemo(
    () => documentVersions.find((version) => version.id === activeVersionId)
      ?? documentVersions.find((version) => version.id === documentProject?.currentVersionId)
      ?? documentVersions.find((version) => version.fileId === chosenSourceFileId)
      ?? documentVersions[0]
      ?? null,
    [activeVersionId, chosenSourceFileId, documentProject?.currentVersionId, documentVersions],
  );
  const activeSourceFileId = activeVersion?.fileId ?? chosenSourceFileId;
  const activePreviewMarkerFields = useMemo(
    () => activeVersion?.id ? markerFieldsByVersion[activeVersion.id] ?? [] : [],
    [activeVersion?.id, markerFieldsByVersion],
  );
  const handleSelectVersion = useCallback((versionId: string) => {
    const version = documentVersions.find((candidate) => candidate.id === versionId);
    if (!version) {
      console.warn('[WizardVersionRail] selected version not found', {
        selectedVersionId: versionId,
        projectId: documentProject?.id ?? null,
        versionIds: documentVersions.map((candidate) => candidate.id),
      });
      return;
    }
    if (documentProject && String(version.projectId) !== String(documentProject.id)) {
      console.error('[WizardVersionRail] selected version group mismatch', {
        selectedVersionId: version.id,
        versionProjectId: version.projectId,
        projectId: documentProject.id,
      });
      return;
    }
    setActiveVersionId(version.id);
  }, [documentProject, documentVersions]);

  useEffect(() => {
    if (!activeVersion) return;
    if (documentProject && String(activeVersion.projectId) !== String(documentProject.id)) {
      console.error('[WizardVersionRail] active version group mismatch', {
        activeVersionId: activeVersion.id,
        versionProjectId: activeVersion.projectId,
        projectId: documentProject.id,
      });
    }
  }, [activeVersion, documentProject]);

  useEffect(() => {
    if (!chosenSourceFileId) {
      setDocumentProject(null);
      setActiveVersionId(null);
      setMarkerFieldsByVersion({});
      setEditBatch(null);
      setAiEditPrompt('');
      setAiEditError(null);
      return;
    }
    let cancelled = false;
    setDocumentProject(null);
    setActiveVersionId(`file:${chosenSourceFileId}`);
    setMarkerFieldsByVersion({ [`file:${chosenSourceFileId}`]: [] });
    setEditBatch(null);
    setAiEditPrompt('');
    setAiEditError(null);
    projectApi.getProjectByFile(chosenSourceFileId, getToken)
      .then((project) => {
        if (cancelled || !project) return;
        const selected = project.versions.find((version) => version.fileId === chosenSourceFileId)
          ?? project.versions.find((version) => version.id === project.currentVersionId)
          ?? project.versions[0]
          ?? null;
        setDocumentProject(project);
        if (selected) {
          setActiveVersionId(selected.id);
          setMarkerFieldsByVersion((prev) => ({ ...prev, [selected.id]: prev[selected.id] ?? [] }));
        }
        wizardEditDebugLog('canonical-project-loaded', {
          sourceFileId: chosenSourceFileId,
          projectId: project.id,
          selectedVersionId: selected?.id ?? null,
          versionCount: project.versions.length,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[Wizard] canonical project lookup failed', {
          sourceFileId: chosenSourceFileId,
          message: err instanceof Error ? err.message : String(err),
        });
      });
    return () => { cancelled = true; };
  }, [chosenSourceFileId, getToken]);

  useEffect(() => {
    const prev = runIdPreviousRef.current;
    if (prev && !runId) {
      if (preservePreviewOnRunClearRef.current) {
        preservePreviewOnRunClearRef.current = false;
        setStep('preview');
        runIdPreviousRef.current = runId;
        return;
      }
      setStep('recipients');
      setDocumentProject(null);
      setActiveVersionId(null);
      setMarkerFieldsByVersion({});
      setEditBatch(null);
      setAiEditPrompt('');
      setAiEditError(null);
      setRecipient({ to: { email: '', name: '' }, cc: [], subject: '', message: '' });
      setExtraDeltaValues({});
    }
    runIdPreviousRef.current = runId;
  }, [runId]);

  const handleGoBackFromFailedRun = useCallback(() => {
    preservePreviewOnRunClearRef.current = true;
    setStep('preview');
    onGoBack?.();
  }, [onGoBack]);

  // Auto-fill Subject when the user picks a source file, unless they've already
  // typed one. The default is "Sign: <doc name>" which the user can override.
  useEffect(() => {
    if (recipient.subject || !chosenSourceLabel) return;
    setRecipient((prev) => ({ ...prev, subject: `Sign: ${chosenSourceLabel}` }));
  }, [chosenSourceLabel, recipient.subject]);

  // ── Run streaming (only after submit) ───────────────────────────
  const { run } = useWorkflowRunStream({ runId, getToken });
  const isTerminal = run?.status === 'completed' || run?.status === 'failed' || run?.status === 'canceled';

  const chatPostedRunRef = useRef<string | null>(null);
  useEffect(() => {
    if (!onAppendThreadSummary || !run || run.status !== 'completed') return;
    if (chatPostedRunRef.current === run.id) return;
    chatPostedRunRef.current = run.id;
    onAppendThreadSummary(buildWorkflowThreadSummary(run));
  }, [run, onAppendThreadSummary]);

  // The dispatch step's output carries the signingRequestId once the
  // run completes. The wizard pulls it out here so the save-as-workflow
  // prompt can forward it to the backend (the existing
  // /api/workflows/from-contract-send endpoint takes signingRequestId,
  // not workflowId — it reconstructs everything from the request).
  const dispatchSigningRequestId = useMemo<string | undefined>(() => {
    if (run?.status !== 'completed') return undefined;
    const dispatchNode = workingDef.graph.nodes.find((n) => n.type === 'dispatch_for_signature');
    if (!dispatchNode) return undefined;
    const dispatchStep = run.steps?.find((s) => s.nodeId === dispatchNode.nodeId);
    const out = dispatchStep?.output as { signingRequestId?: unknown } | null | undefined;
    return typeof out?.signingRequestId === 'string' ? out.signingRequestId : undefined;
  }, [run?.status, run?.steps, workingDef]);

  // Local dismissal state — once the user clicks "Not now" we don't
  // re-prompt for the rest of this wizard session.
  const [savePromptDismissed, setSavePromptDismissed] = useState(false);
  const stageStatuses = useMemo(
    () => mapStagesToStatuses(workingDef, run?.steps ?? []),
    [workingDef, run?.steps],
  );

  // #region agent log
  useEffect(() => {
    if (!runId || !run) return;
    fetch('http://127.0.0.1:7675/ingest/4eb987b4-fa77-42e3-95cf-87b64e13ddeb', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'fbf77c' },
      body: JSON.stringify({
        sessionId: 'fbf77c',
        hypothesisId: 'H-strip-ui',
        location: 'ContractRunWizard.tsx:stageStatuses',
        message: 'wizard_stage_strip',
        data: {
          runStatus: run.status,
          stageStatuses,
          stepSnapshots: run.steps.map((s) => ({ n: s.nodeId, st: s.status })),
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
  }, [runId, run, stageStatuses]);
  // #endregion

  const handleEditBatchChange = useCallback((next: EditBatch) => {
    const acceptedCount = next.edits.filter((edit) => edit.status === 'accepted').length;
    const fileId = next.resultingVersion.fileId;
    const resultingVersion = next.resultingVersion;
    console.log('[Wizard] handleEditBatchChange', {
      documentId: next.docId,
      resultingFileId: fileId ?? null,
      resultingVersion: next.resultingVersion?.version ?? null,
      acceptedCount,
      versionsBefore: documentVersions.map((version) => version.fileId),
      activeVersionId,
      activeSourceFileId: activeSourceFileId ?? null,
      resultingProjectId: next.resultingProject?.id ?? null,
      resultingVersionId: next.resultingVersionId ?? null,
    });
    if (!fileId) {
      setEditBatch(next);
      return;
    }

    const canonicalProject = next.resultingProject ?? null;
    const canonicalVersion = canonicalProject?.versions.find((version) => (
      version.id === next.resultingVersionId || version.fileId === fileId
    )) ?? null;
    const versionId = canonicalVersion?.id ?? next.resultingVersionId ?? `file:${fileId}`;
    const now = new Date().toISOString();
    const projectId = canonicalProject?.id ?? documentProject?.id ?? workflowId;
    const fallbackVersion: DocumentVersionSummary = canonicalVersion ?? {
      id: versionId,
      projectId,
      versionNumber: Number(resultingVersion.version ?? documentVersions.length + 1),
      fileId,
      fileName: resultingVersion.filename ?? next.sourceFilename ?? 'Edited document.pdf',
      mimeType: resultingVersion.mimeType ?? next.sourceMimeType ?? 'application/pdf',
      source: 'ai_edit',
      lineageKind: 'version',
      createdBy: auth.user?.id ?? 'local',
      createdFromVersionId: next.baseVersionId ?? activeVersion?.id ?? null,
      changeSummary: acceptedCount > 0
        ? `Applied ${acceptedCount} accepted edit${acceptedCount === 1 ? '' : 's'}.`
        : 'AI edited version.',
      conversationId: next.conversationId ?? null,
      createdAt: now,
    };
    const projectWithResultingVersion: DocumentProjectDetail = canonicalProject
      ? {
          ...canonicalProject,
          currentVersionId: fallbackVersion.id,
          currentVersion: fallbackVersion,
          versionCount: canonicalProject.versions.some((version) => version.id === fallbackVersion.id || version.fileId === fallbackVersion.fileId)
            ? canonicalProject.versions.length
            : canonicalProject.versions.length + 1,
          versions: canonicalProject.versions.some((version) => version.id === fallbackVersion.id || version.fileId === fallbackVersion.fileId)
            ? canonicalProject.versions.map((version) => (
                version.id === fallbackVersion.id || version.fileId === fallbackVersion.fileId
                  ? { ...version, ...fallbackVersion }
                  : version
              ))
            : [...canonicalProject.versions, fallbackVersion],
        }
      : documentProject
        ? {
            ...documentProject,
            currentVersionId: fallbackVersion.id,
            currentVersion: fallbackVersion,
            versionCount: documentProject.versions.some((version) => version.id === fallbackVersion.id || version.fileId === fallbackVersion.fileId)
              ? documentProject.versions.length
              : documentProject.versions.length + 1,
            updatedAt: now,
            versions: documentProject.versions.some((version) => version.id === fallbackVersion.id || version.fileId === fallbackVersion.fileId)
              ? documentProject.versions.map((version) => (
                  version.id === fallbackVersion.id || version.fileId === fallbackVersion.fileId
                    ? { ...version, ...fallbackVersion }
                    : version
                ))
              : [...documentProject.versions, fallbackVersion],
          }
        : {
            id: projectId,
            title: fallbackVersion.fileName,
            folderId: null,
            primaryConversationId: next.conversationId ?? workflowId,
            currentVersionId: fallbackVersion.id,
            currentVersion: fallbackVersion,
            versionCount: 1,
            createdFrom: 'workflow',
            lastActivityAt: now,
            createdAt: now,
            updatedAt: now,
            versions: [fallbackVersion],
            conversations: [],
          };
    setDocumentProject(projectWithResultingVersion);
    pendingWizardApplyFileRef.current = {
      fileId,
      previousFileId: activeSourceFileId ?? null,
      batchId: next.id,
      createdAt: Date.now(),
      warned: false,
    };
    console.log('[Wizard] apply-select', {
      batchId: next.id,
      resultingFileId: fileId,
      selectedFileIdBefore: activeSourceFileId ?? null,
      selectedFileIdAfter: fileId,
      versionId,
      projectHadVersion: Boolean(canonicalVersion),
    });
    console.log('[DocVersionSync] after-edit', {
      previousDocumentId: activeSourceFileId ?? null,
      previousVersion: activeVersion?.id ?? null,
      newDocumentId: fileId,
      newVersion: versionId,
      previousGroupId: documentProject?.id ?? workflowId,
      newGroupId: canonicalProject?.id ?? documentProject?.id ?? workflowId,
      selectedVersionId: versionId,
      projectHadVersion: Boolean(canonicalVersion),
    });
    setMarkerFieldsByVersion((prev) => ({
      ...prev,
      [versionId]: prev[versionId] ?? [],
    }));
    setActiveVersionId(versionId);
    setEditBatch(next);
  }, [activeSourceFileId, activeVersion?.id, activeVersionId, auth.user?.id, documentProject, documentVersions, workflowId]);

  const handleEditBatchRecoveryVersionReady = useCallback((result: DocEditRecoveryResult) => {
    const project = result.project ?? null;
    const version = project?.versions.find((candidate) => (
      candidate.id === result.versionId ||
      candidate.id === result.recovery.selectedVersionId ||
      candidate.fileId === result.fileId
    )) ?? null;
    if (project) setDocumentProject(project);
    if (version) {
      setMarkerFieldsByVersion((prev) => ({
        ...prev,
        [version.id]: prev[version.id] ?? [],
      }));
      setActiveVersionId(version.id);
      pendingWizardApplyFileRef.current = {
        fileId: version.fileId,
        previousFileId: activeSourceFileId ?? null,
        batchId: result.editBatch.id,
        createdAt: Date.now(),
        warned: false,
      };
    }
    setEditBatch(result.editBatch);
    if (result.hasEditableDoc && version) {
      const token = getToken();
      if (token) {
        axios.get<{
          paragraphs: string[];
        }>(`/api/orchestrate/working-document/${encodeURIComponent(version.fileId)}/paragraphs`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((res) => {
          setEditableDraftHtmlByVersion((prev) => ({
            ...prev,
            [version.id]: paragraphsToSimpleDraftHtml(res.data.paragraphs),
          }));
          console.log('[DocEditRecovery] editable-doc-opened', {
            surface: 'wizard',
            batchId: result.editBatch.id,
            fileId: version.fileId,
            versionId: version.id,
            paragraphCount: res.data.paragraphs.length,
          });
        }).catch((err: unknown) => {
          console.warn('[DocEditRecovery] editable-doc-open-failed', {
            surface: 'wizard',
            batchId: result.editBatch.id,
            fileId: version.fileId,
            versionId: version.id,
            message: err instanceof Error ? err.message : String(err),
          });
        });
      }
    }
    console.log('[DocEditRecovery] editable-draft-selected', {
      batchId: result.editBatch.id,
      fileId: result.fileId,
      versionId: version?.id ?? result.versionId ?? null,
      projectId: project?.id ?? null,
    });
  }, [activeSourceFileId, getToken]);

  useEffect(() => {
    const pending = pendingWizardApplyFileRef.current;
    if (!pending) return;
    const visibleFileId = activeSourceFileId ?? null;
    if (visibleFileId === pending.fileId) {
      console.log('[Wizard] apply-select', {
        batchId: pending.batchId,
        resultingFileId: pending.fileId,
        selectedFileIdBefore: pending.previousFileId,
        selectedFileIdAfter: visibleFileId,
        visibleMatched: true,
      });
      console.log('[DocSurface] after-apply', {
        surface: 'wizard',
        visibleFileId,
        expected: pending.fileId,
        matched: true,
      });
      pendingWizardApplyFileRef.current = null;
      return;
    }
    if (!pending.warned && Date.now() - pending.createdAt > 250) {
      pending.warned = true;
      console.warn('[DocEditApply] visible file mismatch after apply', {
        visibleFileId,
        resultingVersionFileId: pending.fileId,
        batchId: pending.batchId,
        surface: 'wizard',
      });
      console.warn('[DocSurface] after-apply', {
        surface: 'wizard',
        visibleFileId,
        expected: pending.fileId,
        matched: false,
      });
    }
  }, [activeSourceFileId]);

  const handleAskAiEdit = useCallback(async () => {
    const prompt = aiEditPrompt.trim();
    if (!prompt || !activeSourceFileId || aiEditing) return;
    const token = getToken();
    if (!token) {
      setAiEditError('Sign in again to revise this document.');
      return;
    }

    setAiEditing(true);
    setAiEditError(null);
    setEditBatch(null);
    try {
      wizardEditDebugLog('submit', {
        conversationId: null,
        workflowId,
        activeDocumentId: activeSourceFileId,
        activeDocumentVersion: activeVersion?.id ?? null,
        sourceFileId: chosenSourceFileId,
        mode: 'workflow-wizard-preview',
        promptPreview: prompt.slice(0, 120),
      });
      wizardEditDebugLog('base-doc', {
        clonedFromDocumentId: chosenSourceFileId,
        workingDocumentId: activeSourceFileId,
        workingDocumentVersion: activeVersion?.id ?? null,
        esignDraftId: null,
        recipientCount: 1 + recipient.cc.filter(Boolean).length,
        fieldCount: activePreviewMarkerFields.length,
      });
      const fd = new FormData();
      fd.append('message', `Edit this document: ${prompt}`);
      fd.append('currentDocumentId', activeSourceFileId);
      fd.append('currentDocumentName', activeVersion?.fileName ?? chosenSourceLabel ?? 'Contract');
      fd.append('currentDocumentMimeType', activeVersion?.mimeType ?? 'application/pdf');
      const res = await fetch('/api/orchestrate', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok || !res.body) throw new Error('Could not start document revision.');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let proposed = false;
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() ?? '';
        for (const raw of events) {
          const line = raw.split('\n').find((candidate) => candidate.startsWith('data: '));
          if (!line) continue;
          const event = JSON.parse(line.slice(6)) as { type?: string; result?: { editBatch?: EditBatch }; message?: string };
          if (event.type === 'step_complete' && event.result?.editBatch) {
            proposed = true;
            setEditBatch(event.result.editBatch);
          }
          if (event.type === 'error') {
            throw new Error(event.message ?? 'Could not revise this document.');
          }
        }
      }
      if (!proposed) {
        setAiEditError('Corner did not find any specific edits to stage for that request.');
      } else {
        setAiEditPrompt('');
      }
    } catch (err) {
      setAiEditError(err instanceof Error ? err.message : 'Could not revise this document.');
    } finally {
      setAiEditing(false);
    }
  }, [
    activePreviewMarkerFields.length,
    activeSourceFileId,
    activeVersion?.fileName,
    activeVersion?.id,
    activeVersion?.mimeType,
    aiEditing,
    aiEditPrompt,
    chosenSourceFileId,
    chosenSourceLabel,
    getToken,
    recipient.cc,
    workflowId,
  ]);

  // ── Validation per screen ───────────────────────────────────────
  const recipientValid = isEmail(recipient.to.email) && recipient.to.name.trim().length > 0;
  const sourceValid = isResolvedWorkflowSourceFileId(chosenSourceFileId);
  const hasWizardSenderMarkers = useMemo(
    () => activePreviewMarkerFields.some((f) => f.party === 'self' || f.signer === 'you'),
    [activePreviewMarkerFields],
  );
  const canFinalize =
    recipientValid && sourceValid && !submitting && !runId && !applyingWizardMarks;

  // ── Submit ──────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!canFinalize || !primaryRecipient) return;
    setSubmitting(true);
    try {
      const initialVariables: Record<string, unknown> = {
        [primaryRecipient.emailKey]: recipient.to.email.trim(),
        [primaryRecipient.nameKey]: recipient.to.name.trim(),
      };

      // Threads for the second+ recipient if the workflow is multi-party —
      // for v1 we treat extras as Cc unless their slot is explicitly required.
      // (Multi-party authoring is deferred per scoping.)
      for (let i = 1; i < derived.recipients.length; i += 1) {
        const r = derived.recipients[i];
        const cc = recipient.cc[i - 1];
        if (cc) {
          initialVariables[r.emailKey] = cc;
          initialVariables[r.nameKey] = cc;
        }
      }

      // Forward extra delta inputs.
      for (const [k, v] of Object.entries(extraDeltaValues)) {
        if (v.trim()) initialVariables[k] = v.trim();
      }

      // Forward the email-envelope variables — the dispatch step picks them up
      // through its DispatchForSignatureNodeConfig.message (already wired) and
      // future versions of the dispatch step can read additional keys.
      if (recipient.subject.trim()) initialVariables.dispatch_subject = recipient.subject.trim();
      if (recipient.message.trim()) initialVariables.dispatch_message = recipient.message.trim();
      if (recipient.cc.length > 0) initialVariables.dispatch_cc = recipient.cc;

      const markers = activePreviewMarkerFields
        .filter(isRecipientPlacementField)
        .map((field) => ({
          ...field,
          party: 'other' as const,
          signer: 'other' as const,
          placed: false,
          signedAt: undefined,
        }));
      if (markers.length > 0) {
        initialVariables.dispatch_recipient_marker_fields = markers;
      }
      const senderMarkers = activePreviewMarkerFields.filter(
        (field) => field.party === 'self' || field.signer === 'you',
      );
      const senderMarksAlreadyApplied =
        senderMarkers.length > 0 &&
        senderMarkers.every((field) => field.placed || field.signedAt || field.state === 'signed');
      if (senderMarkers.length === 0 || senderMarksAlreadyApplied) {
        initialVariables.dispatch_skip_apply_signature = true;
      }

      await onRun({
        workflowId,
        initialVariables,
        documentIds: isResolvedWorkflowSourceFileId(activeSourceFileId)
          ? [activeSourceFileId]
          : [],
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    activePreviewMarkerFields,
    activeSourceFileId,
    canFinalize,
    derived.recipients,
    extraDeltaValues,
    onRun,
    primaryRecipient,
    recipient,
    workflowId,
  ]);

  const handleWizardApplyEmbeddedMarks = useCallback(async () => {
    const fid = activeSourceFileId;
    if (!fid || !isResolvedWorkflowSourceFileId(fid) || applyingWizardMarks) return;
    if (!hasWizardSenderMarkers) {
      toast({
        variant: 'destructive',
        title: 'Place your fields first',
        description:
          'In the signer legend choose yourself, then add Signature, Date, Name, or Initial markers on the PDF. Use Finalize & Dispatch after your preview looks right.',
      });
      return;
    }
    const token = getToken();
    if (!token) {
      toast({ variant: 'destructive', title: 'Sign in again', description: 'Your session expired.' });
      return;
    }
    setApplyingWizardMarks(true);
    try {
      const pdfRes = await fetch(`/api/file/${encodeURIComponent(fid)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!pdfRes.ok) {
        throw new Error(`Could not load PDF (${pdfRes.status}).`);
      }
      const blob = await pdfRes.blob();
      const fileName = activeVersion?.fileName ?? 'document.pdf';
      const file = new File([blob], fileName, { type: blob.type || 'application/pdf' });
      const fd = new FormData();
      fd.append('files', file);
      fd.append('tool', 'esign');
      fd.append(
        'params',
        JSON.stringify({
          fields: activePreviewMarkerFields,
          currentDocumentId: fid,
        }),
      );
      const res = await axios.post<ToolResult & { fields?: SignatureField[]; status?: string }>(
        '/api/execute',
        fd,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = res.data;
      const status = data.status ?? 'signed';
      if (status === 'needs_signature_setup') {
        toast({
          title: 'Set up your signature',
          description: 'Open Settings → Signature and save a signature image, then try Apply again.',
        });
        return;
      }
      if (!data.fileId) {
        throw new Error('Apply completed but no PDF was returned.');
      }
      const rawReturned = Array.isArray(data.fields) ? data.fields : [];
      const byApiId = new Map(rawReturned.map((f) => [f.id, f]));
      const merged = activePreviewMarkerFields.map((f) =>
        byApiId.has(f.id) ? { ...f, ...byApiId.get(f.id)! } : f,
      );
      setChosenSourceFileId(data.fileId);
      setChosenSourceLabel(data.fileName ?? fileName);
      const project = await projectApi.getProjectByFile(data.fileId, getToken).catch(() => null);
      const selected = project?.versions.find((version) => version.fileId === data.fileId)
        ?? project?.versions.find((version) => version.id === project.currentVersionId)
        ?? null;
      if (project) setDocumentProject(project);
      const nextVersionId = selected?.id ?? `file:${data.fileId}`;
      setActiveVersionId(nextVersionId);
      setMarkerFieldsByVersion((mv) => ({
        ...mv,
        [nextVersionId]: merged,
      }));
      toast({
        title: 'Applied to preview',
        description:
          'Sender-side markers are embedded in a new PDF version. Recipient markers stay for the signing invite.',
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not apply markers.';
      toast({ variant: 'destructive', title: 'Apply failed', description: msg });
    } finally {
      setApplyingWizardMarks(false);
    }
  }, [
    activePreviewMarkerFields,
    activeSourceFileId,
    activeVersion?.fileName,
    applyingWizardMarks,
    getToken,
    hasWizardSenderMarkers,
  ]);

  // ── Reorder + delete steps from the FlowStepsPanel ──────────────
  // Optimistic: mutate local copy first, then PATCH; revert on failure.
  // Disabled while a run is active (runId set) — once execution starts
  // the workflow shape is committed for the run.
  const canEditSteps = !runId;

  const handleReorderSteps = useCallback(async (fromIndex: number, toIndex: number) => {
    if (!canEditSteps) return;
    const previous = workingDef;
    const next = reorderSteps(previous, fromIndex, toIndex);
    if (next === previous) return;
    setWorkingDef(next);
    try {
      await updateUserWorkflow(workflowId, { definition: next });
    } catch (err) {
      console.error('[wizard] reorder persist failed; reverting', err);
      setWorkingDef(previous);
    }
  }, [canEditSteps, workingDef, workflowId]);

  const handleDeleteStep = useCallback(async (nodeId: string) => {
    if (!canEditSteps) return;
    const previous = workingDef;
    const next = removeStep(previous, nodeId);
    if (next === previous) return;
    setWorkingDef(next);
    try {
      await updateUserWorkflow(workflowId, { definition: next });
    } catch (err) {
      console.error('[wizard] delete-step persist failed; reverting', err);
      setWorkingDef(previous);
    }
  }, [canEditSteps, workingDef, workflowId]);

  const showRecipientsModal = !runId && step === 'recipients';
  const fullBleedDocumentSteps = !runId && (step === 'source' || step === 'preview');

  // ── Render ──────────────────────────────────────────────────────
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: 0 }}>
      {showRecipientsModal && (
        <div
          style={RECIPIENTS_MODAL_BACKDROP}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose?.();
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="contract-send-recipients-title"
        >
          <div
            style={RECIPIENTS_MODAL_CARD}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={RECIPIENTS_MODAL_HEADER_ROW}>
              <div style={{ minWidth: 0 }}>
                <div id="contract-send-recipients-title" style={RECIPIENTS_MODAL_TITLE}>
                  {workingDef.label}
                </div>
                <div style={RECIPIENTS_MODAL_SUB}>Step 1 of 3 · Recipients</div>
              </div>
              {onClose && (
                <button type="button" aria-label="Close" onClick={onClose} style={MODAL_CLOSE_BTN}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
            <div style={{ padding: '0 20px 20px' }}>
              <RecipientsScreen
                variant="modal"
                value={recipient}
                primaryRecipient={primaryRecipient}
                onChange={setRecipient}
                onContinue={() => setStep('source')}
                canContinue={recipientValid}
              />
            </div>
          </div>
        </div>
      )}

      {!showRecipientsModal && (
        <div style={CONTAINER}>
          <WizardTopBar
            definition={workingDef}
            step={step}
            runActive={!!runId}
            terminal={isTerminal}
            onClose={onClose}
            onRunAgain={onRunAgain && isTerminal ? onRunAgain : undefined}
            flowPanelOpen={flowPanelOpen}
            onToggleFlowPanel={() => setFlowPanelOpen((p) => !p)}
          />

          <div style={BODY_ROW}>
            <div
              style={{
                ...MAIN,
                ...(fullBleedDocumentSteps
                  ? {
                      padding: step === 'preview' ? '12px 16px' : '16px 20px',
                      alignItems: 'stretch' as const,
                      justifyContent: 'flex-start' as const,
                      overflow: 'hidden',
                    }
                  : {}),
              }}
            >
              {!runId && (
                <>
                  {step === 'source' && (
                    <DocumentSourceScreen
                      fullBleed
                      defaultSourceFileId={derived.defaultSourceFileId}
                      chosenSourceFileId={chosenSourceFileId}
                      templates={templates}
                      onPick={(id, label) => {
                        setChosenSourceFileId(id);
                        setChosenSourceLabel(label ?? null);
                      }}
                      onBack={() => setStep('recipients')}
                      onContinue={() => setStep('preview')}
                      canContinue={sourceValid}
                    />
                  )}

                  {step === 'preview' && (
                    <TemplatePreviewScreen
                      derived={derived}
                      recipient={recipient}
                      onRecipientChange={setRecipient}
                      chosenSourceFileId={activeSourceFileId}
                      documentVersions={documentVersions}
                      activeVersionId={activeVersion?.id ?? null}
                      onSelectVersion={handleSelectVersion}
                      extraDeltaValues={extraDeltaValues}
                      onExtraDeltaChange={(key, value) =>
                        setExtraDeltaValues((prev) => ({ ...prev, [key]: value }))
                      }
                      previewMarkerFields={activePreviewMarkerFields}
                      editableDraftHtml={activeVersion?.id ? editableDraftHtmlByVersion[activeVersion.id] ?? null : null}
                      onPreviewMarkerFieldsChange={(updater) => {
                        if (!activeVersion?.id) return;
                        setMarkerFieldsByVersion((prev) => {
                          const current = prev[activeVersion.id] ?? [];
                          const next = typeof updater === 'function'
                            ? (updater as (value: SignatureField[]) => SignatureField[])(current)
                            : updater;
                          return { ...prev, [activeVersion.id]: next };
                        });
                      }}
                      editBatch={editBatch}
                      onEditBatchChange={handleEditBatchChange}
                      onEditBatchRecoveryVersionReady={handleEditBatchRecoveryVersionReady}
                      aiEditPrompt={aiEditPrompt}
                      onAiEditPromptChange={setAiEditPrompt}
                      onAskAiEdit={handleAskAiEdit}
                      aiEditing={aiEditing}
                      aiEditError={aiEditError}
                      senderSigner={{
                        name: auth.user?.displayName ?? 'You',
                        email: auth.user?.email ?? 'me',
                      }}
                      onBack={() => setStep('source')}
                      onSend={handleSubmit}
                      canSend={canFinalize}
                      submitting={submitting}
                      getToken={getToken}
                      fullBleed
                      onApplyEmbeddedMarks={handleWizardApplyEmbeddedMarks}
                      applyEmbeddedMarksBusy={applyingWizardMarks}
                      applyEmbeddedMarksDisabled={activePreviewMarkerFields.length === 0}
                    />
                  )}
                </>
              )}

              {runId && (
                <RunProgressView
                  status={run?.status}
                  errorMessage={run?.error ?? undefined}
                  stageStatuses={stageStatuses}
                  onClose={onClose}
                  onRunAgain={onRunAgain}
                  onGoBack={onGoBack ? handleGoBackFromFailedRun : undefined}
                  saveAsMode={saveAsMode}
                  saveAsWorkflowName={saveAsWorkflowName}
                  saveAsDefaultName={saveAsDefaultName ?? chosenSourceLabel ?? undefined}
                  savePromptDismissed={savePromptDismissed}
                  onDismissSavePrompt={() => setSavePromptDismissed(true)}
                  onSaveAsWorkflow={onSaveAsWorkflow ? async (name) => {
                    await onSaveAsWorkflow({
                      name,
                      mode: saveAsMode ?? 'workflow',
                      signingRequestId: dispatchSigningRequestId,
                      sourceFileId: activeSourceFileId ?? undefined,
                    });
                    // Treat a successful save as the same as dismissing — the
                    // prompt should disappear so the user knows it took.
                    setSavePromptDismissed(true);
                  } : undefined}
                />
              )}
            </div>

            {flowPanelOpen && (
              <FlowStepsPanel
                definition={workingDef}
                canEdit={canEditSteps}
                onReorderSteps={handleReorderSteps}
                onDeleteStep={handleDeleteStep}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stage progress mapping ────────────────────────────────────────────────

interface ProgressStage {
  id: 'clone' | 'fill' | 'sign' | 'dispatch';
  label: string;
  matchType: WorkflowDefinition['graph']['nodes'][number]['type'];
}

const STAGES: ProgressStage[] = [
  { id: 'clone', label: 'Clone', matchType: 'clone_document' },
  { id: 'fill', label: 'Fill', matchType: 'fill_placeholders' },
  { id: 'sign', label: 'Sign', matchType: 'apply_signature' },
  { id: 'dispatch', label: 'Dispatch', matchType: 'dispatch_for_signature' },
];

function mapStagesToStatuses(
  definition: WorkflowDefinition,
  steps: WorkflowRunStep[],
): Record<ProgressStage['id'], 'pending' | 'running' | 'completed' | 'failed'> {
  const result: Record<ProgressStage['id'], 'pending' | 'running' | 'completed' | 'failed'> = {
    clone: 'pending',
    fill: 'pending',
    sign: 'pending',
    dispatch: 'pending',
  };
  for (const stage of STAGES) {
    const node = definition.graph.nodes.find((n) => n.type === stage.matchType);
    if (!node) continue;
    const stepState = steps.find((s) => s.nodeId === node.nodeId);
    if (!stepState) continue;
    if (stepState.status === 'completed') result[stage.id] = 'completed';
    else if (stepState.status === 'running') result[stage.id] = 'running';
    else if (stepState.status === 'failed') result[stage.id] = 'failed';
  }
  return result;
}

// ─── Top bar with step pips ────────────────────────────────────────────────

function WizardTopBar({
  definition,
  step,
  runActive,
  terminal,
  onClose,
  onRunAgain,
  flowPanelOpen,
  onToggleFlowPanel,
}: {
  definition: WorkflowDefinition;
  step: WizardStep;
  runActive: boolean;
  terminal: boolean;
  onClose?: () => void;
  onRunAgain?: () => void;
  flowPanelOpen: boolean;
  onToggleFlowPanel: () => void;
}) {
  const stepIndex = step === 'recipients' ? 0 : step === 'source' ? 1 : 2;
  const labels: Array<{ id: WizardStep; label: string }> = [
    { id: 'recipients', label: 'Recipients' },
    { id: 'source', label: 'Document' },
    { id: 'preview', label: 'Preview & send' },
  ];

  return (
    <div style={TOP_BAR}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        {onClose && (
          <button type="button" onClick={onClose} style={CRUMB_BTN} aria-label="Back to workflows">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 2L5 7L9 12" stroke="#6B5E52" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontSize: 12, color: APP.textMuted, fontFamily: APP.fontFamily }}>Workflows</span>
          </button>
        )}
        <span style={{ fontSize: 14, fontWeight: 600, color: APP.textPrimary, fontFamily: APP.fontFamily }}>
          {definition.label}
        </span>
      </div>

      {!runActive && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {labels.map((l, idx) => {
            const isActive = idx === stepIndex;
            const isDone = idx < stepIndex;
            return (
              <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div
                  style={{
                    width: isActive ? 24 : 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: isActive
                      ? APP.accentWorkflowPrimary
                      : isDone
                        ? '#1B7A4B'
                        : APP.bgWorkflowConnector,
                    transition: 'all 180ms ease',
                  }}
                />
                {idx < labels.length - 1 && (
                  <div style={{ width: 12, height: 1, backgroundColor: APP.bgWorkflowConnector }} />
                )}
              </div>
            );
          })}
          <span style={{ marginLeft: 6, fontSize: 11, color: APP.textMuted, fontFamily: APP.fontFamily }}>
            Step {stepIndex + 1} of 3
          </span>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {terminal && onRunAgain && (
          <button type="button" onClick={onRunAgain} style={SECONDARY_BTN}>
            Run again
          </button>
        )}
        <button
          type="button"
          onClick={onToggleFlowPanel}
          style={{
            ...CRUMB_BTN,
            backgroundColor: flowPanelOpen ? APP.bgWorkflowConnector : 'transparent',
          }}
          title={flowPanelOpen ? 'Hide flow steps' : 'Show flow steps'}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect x="1" y="1" width="5" height="3" rx="1" stroke="#6B5E52" strokeWidth="1.2" />
            <rect x="1" y="5.5" width="5" height="3" rx="1" stroke="#6B5E52" strokeWidth="1.2" />
            <rect x="1" y="10" width="5" height="3" rx="1" stroke="#6B5E52" strokeWidth="1.2" />
            <path d="M6 7L8 7" stroke="#6B5E52" strokeWidth="1.2" strokeLinecap="round" />
            <path d="M8 2.5L8 11.5" stroke="#6B5E52" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          <span style={{ fontSize: 12, color: APP.textMuted, fontFamily: APP.fontFamily }}>Flow</span>
        </button>
      </div>
    </div>
  );
}

// ─── Screen 1: Recipients (compose-style) ──────────────────────────────────

function RecipientsScreen({
  value,
  primaryRecipient,
  onChange,
  onContinue,
  canContinue,
  variant = 'default',
}: {
  value: RecipientDraft;
  primaryRecipient: DispatchRecipientShape | null;
  onChange: (next: RecipientDraft) => void;
  onContinue: () => void;
  canContinue: boolean;
  /** Nested inside modal chrome — drop outer card frame so borders don’t double. */
  variant?: 'default' | 'modal';
}) {
  const { recipients: recent } = useRecentRecipients({ enabled: true });
  const [showSuggest, setShowSuggest] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showSuggest) return;
    const handler = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setShowSuggest(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSuggest]);

  const filteredSuggestions = useMemo(() => {
    const q = value.to.email.trim().toLowerCase();
    if (!q) return recent.slice(0, 8);
    return recent
      .filter((r) => r.email.toLowerCase().includes(q) || r.name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [recent, value.to.email]);

  const surfaceStyle: CSSProperties =
    variant === 'modal'
      ? {
          ...CARD,
          border: 'none',
          boxShadow: 'none',
          borderRadius: 0,
          backgroundColor: 'transparent',
          maxWidth: '100%',
        }
      : CARD;

  return (
    <section style={surfaceStyle}>
      <header style={CARD_HEADER}>
        <span style={CARD_TITLE}>Who's signing?</span>
        <span style={CARD_HINT}>
          {primaryRecipient
            ? `Your "${primaryRecipient.role}" signer plus an optional Cc list and a custom message.`
            : 'Compose the signing invite. Recipients first, doc next.'}
        </span>
      </header>

      <div style={FIELDS}>
        <FieldRow label="From">
          <div style={READONLY_PILL}>You · via Corner</div>
        </FieldRow>

        <FieldRow label="To" required>
          <div ref={wrapRef} style={{ position: 'relative' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="email"
                value={value.to.email}
                onChange={(e) => {
                  onChange({ ...value, to: { ...value.to, email: e.target.value } });
                  setShowSuggest(true);
                }}
                onFocus={() => setShowSuggest(true)}
                placeholder="recipient@example.com"
                style={{ ...INPUT, flex: 2 }}
              />
              <input
                type="text"
                value={value.to.name}
                onChange={(e) => onChange({ ...value, to: { ...value.to, name: e.target.value } })}
                placeholder="Recipient name"
                style={{ ...INPUT, flex: 1.5 }}
              />
            </div>
            {showSuggest && filteredSuggestions.length > 0 && (
              <div style={SUGGEST_DROPDOWN}>
                {filteredSuggestions.map((r) => (
                  <button
                    key={r.email}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onChange({ ...value, to: { email: r.email, name: r.name || value.to.name } });
                      setShowSuggest(false);
                    }}
                    style={SUGGEST_ROW}
                  >
                    <span style={{ flex: 1, fontWeight: 500, color: APP.textPrimary }}>{r.name || r.email}</span>
                    <span style={{ color: APP.textMuted, fontSize: 11 }}>{r.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </FieldRow>

        <FieldRow label="Cc">
          <CcEditor value={value.cc} onChange={(cc) => onChange({ ...value, cc })} />
        </FieldRow>

        <FieldRow label="Subject">
          <input
            type="text"
            value={value.subject}
            onChange={(e) => onChange({ ...value, subject: e.target.value })}
            placeholder='"Sign: Buyer Agreement"  ·  auto-filled when you pick a doc'
            style={INPUT}
          />
        </FieldRow>

        <FieldRow label="Message">
          <textarea
            value={value.message}
            onChange={(e) => onChange({ ...value, message: e.target.value })}
            placeholder="Optional message to the recipient. Leave blank to use the default."
            rows={4}
            style={{ ...INPUT, resize: 'vertical', minHeight: 96 }}
          />
        </FieldRow>
      </div>

      <div style={ACTIONS}>
        <button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          style={canContinue ? PRIMARY_BTN : PRIMARY_BTN_DISABLED}
        >
          Continue → Document
        </button>
        {!canContinue && (
          <span style={MISSING_HINT}>
            Add a recipient name and a valid email to continue.
          </span>
        )}
      </div>
    </section>
  );
}

function CcEditor({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const v = draft.trim();
    if (!v || !isEmail(v)) return;
    if (!value.includes(v)) onChange([...value, v]);
    setDraft('');
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minHeight: 36, padding: '4px 6px', border: `1px solid ${APP.border}`, borderRadius: 8, backgroundColor: 'var(--app-bg-content)' }}>
      {value.map((email) => (
        <span key={email} style={CHIP}>
          {email}
          <button type="button" onClick={() => onChange(value.filter((e) => e !== email))} style={CHIP_REMOVE} aria-label={`Remove ${email}`}>
            ×
          </button>
        </span>
      ))}
      <input
        type="email"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Backspace' && !draft && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={commit}
        placeholder={value.length === 0 ? 'Add Cc email and press Enter' : ''}
        style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: APP.textPrimary, fontFamily: APP.fontFamily, padding: 4 }}
      />
    </div>
  );
}

// ─── Screen 2: Document Source ─────────────────────────────────────────────

/**
 * Saved template summary shown in the source-doc picker. Lightweight
 * shape so the wizard is decoupled from the backend Template model —
 * the parent fetches templates and maps them into this shape.
 */
export interface WorkflowTemplateSummary {
  templateId: string;
  /** User-given name. */
  name: string;
  /** Master document fileId — what gets passed to onPick. */
  sourceFileId: string;
  /** When the template was last successfully sent. */
  lastUsedAt?: string;
  /** Optional usage count (shown when > 1 to signal trust). */
  useCount?: number;
}

function DocumentSourceScreen({
  defaultSourceFileId,
  chosenSourceFileId,
  templates,
  onPick,
  onBack,
  onContinue,
  canContinue,
  fullBleed = false,
}: {
  defaultSourceFileId: string | null;
  chosenSourceFileId: string | null;
  /** Templates saved against the parent workflow. Empty array hides the
   *  templates section entirely. */
  templates?: WorkflowTemplateSummary[];
  onPick: (sourceFileId: string, label?: string) => void;
  onBack: () => void;
  onContinue: () => void;
  canContinue: boolean;
  /** Use full wizard width instead of a capped column. */
  fullBleed?: boolean;
}) {
  const [tab, setTab] = useState<'knowledge' | 'recent'>('recent');
  const [kb, setKb] = useState<ContractsKnowledgeBase | null>(null);
  const [recent, setRecent] = useState<RecentContract[]>([]);
  const [recentDocs, setRecentDocs] = useState<RecentDocument[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([getContractsKnowledgeBase(), listRecentContracts(10), listRecentDocuments(10)])
      .then(([kbResp, recentResp, docsResp]) => {
        if (cancelled) return;
        setKb(kbResp);
        setRecent(recentResp);
        setRecentDocs(docsResp);
      })
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const knowledgeBaseEmpty = !loading
    && (kb?.folders.length ?? 0) === 0
    && (kb?.files.length ?? 0) === 0
    && (kb?.workflows.length ?? 0) === 0;

  const surfaceStyle: CSSProperties = fullBleed
    ? {
        ...CARD,
        maxWidth: '100%',
        flex: '1 1 auto',
        minHeight: 0,
        alignSelf: 'stretch',
        width: '100%',
      }
    : CARD;

  return (
    <section style={surfaceStyle}>
      <header style={CARD_HEADER}>
        <span style={CARD_TITLE}>Pick a contract</span>
        <span style={CARD_HINT}>
          Choose from your recent documents or your contracts knowledge base.
        </span>
      </header>

      {/* Saved templates for this workflow — the gold path. Surfaced
          ABOVE the tabs so a returning user's eye lands here first.
          Hidden entirely when the workflow has no templates yet (e.g.
          first-time use), to keep the picker uncluttered. */}
      {templates && templates.length > 0 && (
        <div style={TEMPLATES_SECTION}>
          <div style={TEMPLATES_HEADER}>
            <span style={TEMPLATES_HEADER_LABEL}>
              ✨ Saved templates
            </span>
            <span style={TEMPLATES_HEADER_SUB}>
              Skip detection — fields are already where you put them
            </span>
          </div>
          <div style={LIST}>
            {templates.map((t) => (
              <PickRow
                key={t.templateId}
                icon="📑"
                title={t.name}
                subtitle={
                  t.useCount && t.useCount > 1
                    ? `Used ${t.useCount} times`
                    : 'Saved template'
                }
                meta={t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleDateString() : undefined}
                selected={chosenSourceFileId === t.sourceFileId}
                onClick={() => onPick(t.sourceFileId, t.name)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Tab toggle */}
      <div style={TAB_BAR}>
        <button
          type="button"
          onClick={() => setTab('recent')}
          style={tab === 'recent' ? TAB_BTN_ACTIVE : TAB_BTN}
        >
          🕐 Recent
        </button>
        <button
          type="button"
          onClick={() => setTab('knowledge')}
          style={tab === 'knowledge' ? TAB_BTN_ACTIVE : TAB_BTN}
        >
          <AppFolderIcon width={16} height={13} />
          Knowledge base
        </button>
      </div>

      {/* Source default reminder */}
      {defaultSourceFileId && !chosenSourceFileId && (
        <button
          type="button"
          onClick={() => onPick(defaultSourceFileId, 'Original template')}
          style={DEFAULT_PILL}
        >
          ↻ Use the original template this workflow was captured from
        </button>
      )}

      {/* Tab body */}
      {tab === 'knowledge' && (
        <div style={LIST}>
          {loading && <div style={EMPTY_HINT}>Loading…</div>}
          {!loading && knowledgeBaseEmpty && (
            <div style={EMPTY_BOX}>
              <span style={{ fontSize: 14, color: APP.textPrimary, fontWeight: 500 }}>Nothing in your knowledge base yet.</span>
              <span style={{ fontSize: 12, color: APP.textMuted, marginTop: 4 }}>
                Open a contract in the workspace, click <strong>Add to project</strong>, and toggle a project as <strong>Contracts</strong>. Files in those projects show up here.
              </span>
            </div>
          )}

          {/* Captured workflows (the gold-path template list) */}
          {!loading && kb?.workflows && kb.workflows.length > 0 && (
            <SectionHeader>Captured workflow templates</SectionHeader>
          )}
          {!loading && kb?.workflows.map((w) => (
            <PickRow
              key={w.id}
              icon="✨"
              title={w.name}
              subtitle={w.description ?? 'Reusable contract template'}
              meta={new Date(w.updatedAt).toLocaleDateString()}
              selected={chosenSourceFileId === w.sourceFileId && w.sourceFileId !== null}
              onClick={() => {
                if (w.sourceFileId) onPick(w.sourceFileId, w.name);
              }}
              disabled={!w.sourceFileId}
            />
          ))}

          {!loading && kb?.files && kb.files.length > 0 && (
            <SectionHeader>Files in Contracts projects</SectionHeader>
          )}
          {!loading && kb?.files.map((f) => (
            <PickRow
              key={f.fileId}
              icon="📄"
              title={f.fileName}
              subtitle={f.conversationTitle}
              meta={new Date(f.lastUsedAt).toLocaleDateString()}
              selected={chosenSourceFileId === f.fileId}
              onClick={() => onPick(f.fileId, f.fileName)}
            />
          ))}
        </div>
      )}

      {tab === 'recent' && (
        <div style={LIST}>
          {loading && <div style={EMPTY_HINT}>Loading…</div>}
          {!loading && recentDocs.length === 0 && recent.length === 0 && (
            <div style={EMPTY_BOX}>
              <span style={{ fontSize: 14, color: APP.textPrimary, fontWeight: 500 }}>No documents yet.</span>
              <span style={{ fontSize: 12, color: APP.textMuted, marginTop: 4 }}>
                Work on a contract in the chat and it will show up here.
              </span>
            </div>
          )}

          {/* Recent documents from conversation history */}
          {!loading && recentDocs.length > 0 && (
            <SectionHeader>Recent documents</SectionHeader>
          )}
          {!loading && recentDocs.map((d) => (
            <PickRow
              key={d.fileId}
              icon="📄"
              title={d.fileName}
              subtitle={d.conversationTitle}
              meta={new Date(d.lastUsedAt).toLocaleDateString()}
              selected={chosenSourceFileId === d.fileId}
              onClick={() => onPick(d.fileId, d.fileName)}
              badge={d.folder ? { label: d.folder.name, isContracts: d.folder.isContracts } : undefined}
            />
          ))}

          {/* Dispatched contracts */}
          {!loading && recent.length > 0 && (
            <SectionHeader>Dispatched contracts</SectionHeader>
          )}
          {!loading && recent.map((c) => (
            <PickRow
              key={c.requestId}
              icon="📨"
              title={c.fileName}
              subtitle={c.primaryRecipient ? `→ ${c.primaryRecipient.name || c.primaryRecipient.email}` : `${c.recipientCount} recipient${c.recipientCount === 1 ? '' : 's'}`}
              meta={new Date(c.sentAt).toLocaleDateString()}
              selected={chosenSourceFileId === c.sourceFileId}
              onClick={() => onPick(c.sourceFileId, c.fileName)}
            />
          ))}
        </div>
      )}

      <div style={{ ...ACTIONS, justifyContent: 'space-between' }}>
        <button type="button" onClick={onBack} style={SECONDARY_BTN}>
          ← Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {!canContinue && <span style={MISSING_HINT}>Pick a contract to continue.</span>}
          <button
            type="button"
            onClick={onContinue}
            disabled={!canContinue}
            style={canContinue ? PRIMARY_BTN : PRIMARY_BTN_DISABLED}
          >
            Continue → Preview
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── Screen 3: Template Preview ────────────────────────────────────────────

function TemplatePreviewScreen({
  derived,
  recipient,
  onRecipientChange,
  chosenSourceFileId,
  documentVersions,
  activeVersionId,
  onSelectVersion,
  extraDeltaValues,
  onExtraDeltaChange,
  previewMarkerFields,
  editableDraftHtml,
  onPreviewMarkerFieldsChange,
  editBatch,
  onEditBatchChange,
  onEditBatchRecoveryVersionReady,
  aiEditPrompt,
  onAiEditPromptChange,
  onAskAiEdit,
  aiEditing,
  aiEditError,
  senderSigner,
  onBack,
  onSend,
  canSend,
  submitting,
  getToken,
  fullBleed = false,
  onApplyEmbeddedMarks,
  applyEmbeddedMarksBusy = false,
  applyEmbeddedMarksDisabled = false,
}: {
  derived: DerivedGraph;
  recipient: RecipientDraft;
  onRecipientChange: (next: RecipientDraft) => void;
  chosenSourceFileId: string | null;
  documentVersions: DocumentVersionSummary[];
  activeVersionId: string | null;
  onSelectVersion: (id: string) => void;
  extraDeltaValues: Record<string, string>;
  onExtraDeltaChange: (key: string, value: string) => void;
  previewMarkerFields: SignatureField[];
  editableDraftHtml?: string | null;
  onPreviewMarkerFieldsChange: Dispatch<SetStateAction<SignatureField[]>>;
  editBatch: EditBatch | null;
  onEditBatchChange: (batch: EditBatch) => void;
  onEditBatchRecoveryVersionReady: (result: DocEditRecoveryResult) => void;
  aiEditPrompt: string;
  onAiEditPromptChange: (value: string) => void;
  onAskAiEdit: () => void;
  aiEditing: boolean;
  aiEditError: string | null;
  senderSigner: { name: string; email: string };
  onBack: () => void;
  onSend: () => void;
  canSend: boolean;
  submitting: boolean;
  getToken: () => string | null;
  /** Fill available wizard viewport height/width (step 3 full screen). */
  fullBleed?: boolean;
  /** Burn sender-side markers into the PDF via `/api/execute` (preview only). */
  onApplyEmbeddedMarks?: () => void | Promise<void>;
  applyEmbeddedMarksBusy?: boolean;
  applyEmbeddedMarksDisabled?: boolean;
}) {
  const [activeSignerIndex, setActiveSignerIndex] = useState(1);
  const [viewEditPage, setViewEditPage] = useState(1);
  const [pdfZoom, setPdfZoom] = useState(100);
  const activeVersion = useMemo(
    () => documentVersions.find((version) => version.id === activeVersionId) ?? documentVersions[0] ?? null,
    [activeVersionId, documentVersions],
  );

  useEffect(() => {
    setPdfZoom(100);
  }, [chosenSourceFileId, activeVersionId]);

  const signers: ViewerSigner[] = useMemo(
    () => [
      { role: 'self', name: senderSigner.name, email: senderSigner.email },
      {
        role: 'other',
        name: recipient.to.name.trim() || 'Recipient',
        email: recipient.to.email.trim() || 'recipient@example.com',
      },
    ],
    [senderSigner.name, senderSigner.email, recipient.to.name, recipient.to.email],
  );

  useEffect(() => {
    setActiveSignerIndex(1);
  }, [recipient.to.email]);

  useEffect(() => {
    const onViewEditAnchor = (event: Event) => {
      const detail = (event as CustomEvent<{ docId?: string; page?: number }>).detail;
      if (detail?.page) setViewEditPage(detail.page);
    };
    window.addEventListener('corner:view-edit-anchor', onViewEditAnchor);
    return () => window.removeEventListener('corner:view-edit-anchor', onViewEditAnchor);
  }, []);

  const recipientMarkerCount = useMemo(
    () => previewMarkerFields.filter(isRecipientPlacementField).length,
    [previewMarkerFields],
  );

  const handleAddField = useCallback(
    (field: SignatureField) => {
      onPreviewMarkerFieldsChange((prev) => [...prev, field]);
    },
    [onPreviewMarkerFieldsChange],
  );

  const handleDeleteField = useCallback(
    (fieldId: string) => {
      onPreviewMarkerFieldsChange((prev) => prev.filter((f) => f.id !== fieldId));
    },
    [onPreviewMarkerFieldsChange],
  );

  const handleMoveField = useCallback(
    (fieldId: string, rect: { x: number; y: number; width: number; height: number }) => {
      onPreviewMarkerFieldsChange((prev) =>
        prev.map((f) => (f.id === fieldId ? { ...f, ...rect } : f)),
      );
    },
    [onPreviewMarkerFieldsChange],
  );

  const handleConfirmField = useCallback(
    (fieldId: string) => {
      onPreviewMarkerFieldsChange((prev) =>
        prev.map((f) => (f.id === fieldId ? { ...f, state: 'confirmed' as const } : f)),
      );
    },
    [onPreviewMarkerFieldsChange],
  );

  // Compute the recipient-driven baseline overrides so the user sees what
  // the server-side fill is actually going to write.
  const recipientOverrides = useMemo(
    () => applyRecipientToBaseline(
      derived.fillConfig?.baselineValues ?? [],
      recipient.to.name,
      recipient.to.email,
    ),
    [derived.fillConfig, recipient.to.email, recipient.to.name],
  );

  const isActivePdf = isPdfDocumentVersion(activeVersion);
  const isActiveDocx = isDocxDocumentVersion(activeVersion);
  const activePreviewFileId = activeVersion?.fileId ?? chosenSourceFileId;
  const activePreviewUrl =
    activePreviewFileId && isResolvedWorkflowSourceFileId(activePreviewFileId)
      ? `/api/file/${activePreviewFileId}`
      : null;
  const pdfUrl = isActivePdf ? activePreviewUrl : null;
  const docxUrl = isActiveDocx ? activePreviewUrl : null;

  useEffect(() => {
    console.log('[DocSurface] select-version', {
      surface: 'wizard',
      versionId: activeVersionId,
      fileId: activePreviewFileId,
      mimeType: activeVersion?.mimeType ?? null,
      viewer: isActivePdf ? 'pdf' : isActiveDocx ? 'docx' : activePreviewUrl ? 'generic' : 'empty',
    });
  }, [activePreviewFileId, activePreviewUrl, activeVersion?.mimeType, activeVersionId, isActiveDocx, isActivePdf]);

  useEffect(() => {
    if (!DOC_WIZARD_EDIT_DEBUG || !chosenSourceFileId || !isResolvedWorkflowSourceFileId(chosenSourceFileId)) return;
    const token = getToken();
    if (!token) {
      console.log('[WizardReview] activeDoc', {
        docId: chosenSourceFileId,
        docVersion: activeVersionId,
        excerpt: null,
        reason: 'no-token',
      });
      return;
    }
    const controller = new AbortController();
    fetch(`/api/orchestrate/working-document/${encodeURIComponent(chosenSourceFileId)}/paragraphs`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ paragraphs?: string[] }>;
      })
      .then((data) => {
        const paragraphs = Array.isArray(data.paragraphs) ? data.paragraphs : [];
        const likelyEmployeeClause =
          paragraphs.find((paragraph) => /\b(employee|employer|cook|signature|name)\b/i.test(paragraph)) ??
          paragraphs[0] ??
          '';
        console.log('[WizardReview] activeDoc', {
          docId: chosenSourceFileId,
          docVersion: activeVersionId,
          excerpt: likelyEmployeeClause.slice(0, 120),
        });
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.log('[WizardReview] activeDoc', {
          docId: chosenSourceFileId,
          docVersion: activeVersionId,
          excerpt: null,
          reason: err instanceof Error ? err.message : String(err),
        });
      });
    return () => controller.abort();
  }, [activeVersionId, chosenSourceFileId, getToken]);
  const currentSigner = signers[Math.max(0, Math.min(activeSignerIndex, signers.length - 1))];
  const editReviewCounts = useMemo(() => {
    if (!editBatch) return null;
    return {
      total: editBatch.edits.length,
      accepted: editBatch.edits.filter((edit) => edit.status === 'accepted').length,
      rejected: editBatch.edits.filter((edit) => edit.status === 'rejected').length,
      pending: editBatch.edits.filter((edit) => edit.status === 'pending').length,
    };
  }, [editBatch]);

  return (
    <section
      style={{
        ...CARD,
        flex: 1,
        width: '100%',
        maxWidth: fullBleed ? '100%' : 'min(100%, 1740px)',
        minHeight: fullBleed ? 0 : 'clamp(560px, 74vh, 960px)',
        alignSelf: fullBleed ? 'stretch' : undefined,
        padding: 0,
        overflow: 'hidden',
      }}
    >
      <header style={{ ...CARD_HEADER, padding: '20px 24px 12px' }}>
        <span style={CARD_TITLE}>Review &amp; send</span>
        <span style={CARD_HINT}>
          Place recipient markers, then use yourself in the legend to add your Signature / Date / Name markers.
          Click <strong>Apply changes</strong> in the Fields panel to embed sender-side signature, date, name, initial, and title marks into a new PDF version before <strong>Finalize &amp; Dispatch</strong>.
        </span>
      </header>

      <div style={SPLIT}>
        {/* Left: PDF + placement (configure mode) */}
        <div style={PDF_PANE}>
          {pdfUrl ? (
            <div style={VERSIONED_PREVIEW}>
              <DocumentVersionRail
                versions={documentVersions}
                activeVersionId={activeVersionId}
                onSelect={onSelectVersion}
              />
              <div style={{ flex: 1, minHeight: 0, minWidth: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
                <PdfSigningViewer
                  key={`${activePreviewFileId ?? chosenSourceFileId}:${activeVersionId ?? ''}:${viewEditPage}`}
                  pdfUrl={pdfUrl}
                  mimeType={activeVersion?.mimeType}
                  documentId={
                    activePreviewFileId && isResolvedWorkflowSourceFileId(activePreviewFileId)
                      ? activePreviewFileId
                      : undefined
                  }
                  getToken={getToken}
                  fields={previewMarkerFields}
                  mode="configure"
                  signers={signers}
                  activeSignerIndex={activeSignerIndex}
                  onSelectSigner={setActiveSignerIndex}
                  onAddField={handleAddField}
                  onDeleteField={handleDeleteField}
                  onMoveField={handleMoveField}
                  onConfirmField={handleConfirmField}
                  currentSignerId={currentSigner?.email}
                  showThumbnails
                  initialPage={viewEditPage}
                  zoom={pdfZoom}
                  onZoomChange={setPdfZoom}
                  onApplySignature={
                    onApplyEmbeddedMarks
                      ? () => {
                          void onApplyEmbeddedMarks();
                        }
                      : undefined
                  }
                  applySignatureDisabled={
                    applyEmbeddedMarksBusy || applyEmbeddedMarksDisabled
                  }
                />
              </div>
            </div>
          ) : docxUrl && editableDraftHtml ? (
            <div style={VERSIONED_PREVIEW}>
              <DocumentVersionRail
                versions={documentVersions}
                activeVersionId={activeVersionId}
                onSelect={onSelectVersion}
              />
              <div style={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'auto', background: APP.bgDocViewer }}>
                <div
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 4,
                    borderBottom: `1px solid ${APP.border}`,
                    backgroundColor: '#F4F1EC',
                    color: APP.textCardDetail,
                    fontFamily: APP.fontFamily,
                    fontSize: 12,
                    lineHeight: '17px',
                    padding: '8px 14px',
                  }}
                >
                  <strong>Editable copy from signed PDF.</strong> You are now editing a Corner working copy; the original signed PDF is preserved in the version list.
                </div>
                <div style={{ padding: '18px 24px 32px', display: 'flex', justifyContent: 'center' }}>
                  <PaginatedDocument
                    html={editableDraftHtml}
                    pageSize="letter"
                    onAskCornerAboutSelection={(snippet) => {
                      if (snippet.text.trim()) {
                        onAiEditPromptChange(`Edit this selected text: ${snippet.text.trim()}`);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          ) : docxUrl ? (
            <div style={VERSIONED_PREVIEW}>
              <DocumentVersionRail
                versions={documentVersions}
                activeVersionId={activeVersionId}
                onSelect={onSelectVersion}
              />
              <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                <div
                  style={{
                    width: 'min(100%, 620px)',
                    borderRadius: 10,
                    border: `1px solid ${APP.border}`,
                    backgroundColor: APP.bgDocSheet,
                    boxShadow: '0 18px 48px rgba(44, 36, 29, 0.10)',
                    padding: '28px 32px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    fontFamily: APP.fontFamily,
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 750, color: APP.textPrimary }}>
                    Editable copy from signed PDF
                  </span>
                  <span style={{ fontSize: 12, lineHeight: '18px', color: APP.textCardDetail }}>
                    Corner preserved the signed PDF unchanged and created this DOCX branch for edits. Download it to edit in Word or Pages, then reimport it when you are ready.
                  </span>
                  <a
                    href={docxUrl}
                    download={activeVersion?.fileName ?? 'editable-draft.docx'}
                    style={{
                      alignSelf: 'flex-start',
                      borderRadius: 6,
                      backgroundColor: APP.textDark,
                      color: '#FDFCFB',
                      padding: '9px 14px',
                      fontSize: 12,
                      fontWeight: 700,
                      textDecoration: 'none',
                    }}
                  >
                    Download DOCX
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div style={EMPTY_HINT}>Pick a source document to preview.</div>
          )}
        </div>

        {/* Right: Deltas */}
        <div style={DELTAS_PANE}>
          <div style={{ marginBottom: 12 }}>
            <span style={DELTA_GROUP_LABEL}>Signer (edit anytime)</span>
            <p style={{ margin: '0 0 10px', fontSize: 11, color: APP.textMuted, lineHeight: 1.45, fontFamily: APP.fontFamily }}>
              Recipient name and email are kept separate from field placement, the same way DocuSign keeps the recipient list distinct from tagging. Change them here without losing your PDF markers.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <FieldRow label="Recipient email" required>
                <input
                  type="email"
                  value={recipient.to.email}
                  onChange={(e) =>
                    onRecipientChange({ ...recipient, to: { ...recipient.to, email: e.target.value } })
                  }
                  placeholder="recipient@example.com"
                  style={INPUT}
                />
              </FieldRow>
              <FieldRow label="Recipient name" required>
                <input
                  type="text"
                  value={recipient.to.name}
                  onChange={(e) =>
                    onRecipientChange({ ...recipient, to: { ...recipient.to, name: e.target.value } })
                  }
                  placeholder="Legal name on the agreement"
                  style={INPUT}
                />
              </FieldRow>
            </div>
            <button
              type="button"
              onClick={() => onPreviewMarkerFieldsChange([])}
              disabled={recipientMarkerCount === 0}
              style={{
                marginTop: 10,
                ...SECONDARY_BTN,
                fontSize: 12,
                opacity: recipientMarkerCount === 0 ? 0.45 : 1,
                cursor: recipientMarkerCount === 0 ? 'default' : 'pointer',
              }}
            >
              Clear recipient markers ({recipientMarkerCount})
            </button>

            <div style={AI_EDIT_BOX}>
              <span style={DELTA_GROUP_LABEL}>Ask Corner to revise</span>
              <textarea
                value={aiEditPrompt}
                onChange={(e) => onAiEditPromptChange(e.target.value)}
                placeholder="e.g. Change the seller name to Nick and tighten the closing section."
                rows={3}
                style={AI_EDIT_TEXTAREA}
              />
              <button
                type="button"
                onClick={onAskAiEdit}
                disabled={aiEditing || !aiEditPrompt.trim() || !chosenSourceFileId}
                style={{
                  ...(aiEditing || !aiEditPrompt.trim() || !chosenSourceFileId ? SECONDARY_BTN : PRIMARY_BTN),
                  width: '100%',
                  justifyContent: 'center',
                }}
              >
                {aiEditing ? 'Staging edits...' : 'Stage AI edits'}
              </button>
              {aiEditError && (
                <p style={{ margin: 0, fontSize: 11, lineHeight: '16px', color: '#B42318', fontFamily: APP.fontFamily }}>
                  {aiEditError}
                </p>
              )}
            </div>

            {editBatch && (
              <div style={AI_EDIT_REVIEW_BOX}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ ...DELTA_GROUP_LABEL, marginBottom: 0 }}>Review proposed edits</span>
                  {editReviewCounts && (
                    <span style={AI_EDIT_REVIEW_META}>
                      {editReviewCounts.pending} pending · {editReviewCounts.accepted} accepted
                    </span>
                  )}
                </div>
                <p style={AI_EDIT_REVIEW_HINT}>
                  Accept or decline the changes here before updating the preview.
                </p>
                <CoTEditBatchCard
                  batch={editBatch}
                  getToken={getToken}
                  onBatchChange={onEditBatchChange}
                  onRecoveryVersionReady={onEditBatchRecoveryVersionReady}
                  documentVersions={documentVersions}
                  onSelectDocumentVersion={(version) => onSelectVersion(version.id)}
                  artifactFormat="pdf"
                />
              </div>
            )}

            {activeVersion && (activeVersion.versionNumber > 1 || activeVersion.source !== 'original') && recipientMarkerCount === 0 && (
              <p style={{ margin: '10px 0 0', fontSize: 11, lineHeight: '16px', color: APP.textMuted, fontFamily: APP.fontFamily }}>
                This edited version starts with fresh recipient markers. Place or confirm markers on this version before dispatch.
              </p>
            )}

            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              style={{
                marginTop: 12,
                width: '100%',
                ...(canSend ? PRIMARY_BTN_LARGE : PRIMARY_BTN_DISABLED_LARGE),
              }}
            >
              {submitting ? 'Sending…' : 'Finalize & Dispatch →'}
            </button>
          </div>

          {recipientOverrides.size > 0 && (
            <div style={{ marginBottom: 12 }}>
              <span style={DELTA_GROUP_LABEL}>Auto-filled placeholders</span>
              {Array.from(recipientOverrides.entries()).map(([fieldId, val]) => {
                const baseline = derived.fillConfig?.baselineValues.find((b) => b.fieldId === fieldId);
                return (
                  <DeltaPreviewRow
                    key={fieldId}
                    label={baseline?.label ?? baseline?.role ?? fieldId}
                    value={val}
                    locked
                  />
                );
              })}
            </div>
          )}

          {derived.extraDeltas.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <span style={DELTA_GROUP_LABEL}>Editable</span>
              {derived.extraDeltas.map((d) => (
                <div key={d.key} style={DELTA_EDIT_ROW}>
                  <label style={{ fontSize: 12, color: APP.textMuted, fontFamily: APP.fontFamily }}>
                    {d.label}
                  </label>
                  <input
                    type="text"
                    value={extraDeltaValues[d.key] ?? ''}
                    onChange={(e) => onExtraDeltaChange(d.key, e.target.value)}
                    placeholder={d.hint ?? ''}
                    style={INPUT}
                  />
                </div>
              ))}
            </div>
          )}

          {derived.fillConfig && derived.fillConfig.baselineValues.length > recipientOverrides.size && (
            <BaselineDisclosure baseline={derived.fillConfig.baselineValues} overridden={recipientOverrides} />
          )}
        </div>
      </div>

      <div style={{ ...ACTIONS, justifyContent: 'flex-start', padding: '16px 24px 20px', borderTop: `1px solid ${APP.border}` }}>
        <button type="button" onClick={onBack} style={SECONDARY_BTN}>
          ← Back
        </button>
      </div>
    </section>
  );
}

function DocumentVersionRail({
  versions,
  activeVersionId,
  onSelect,
}: {
  versions: DocumentVersionSummary[];
  activeVersionId: string | null;
  onSelect: (id: string) => void;
}) {
  const active = versions.find((version) => version.id === activeVersionId) ?? versions[0] ?? null;
  const selectedVersionId = active?.id ?? null;
  const versionLabel = (version: DocumentVersionSummary) => `v${version.versionNumber ?? '?'}`;
  const isEditableCopyFromSignedPdf = (version: DocumentVersionSummary) =>
    version.source === 'conversion' &&
    version.lineageKind === 'copy' &&
    version.changeSummary === 'Editable copy from signed PDF';
  const sourceLabel = (version: DocumentVersionSummary) =>
    isEditableCopyFromSignedPdf(version)
      ? 'Editable copy from signed PDF'
      : version.source === 'original' ? 'Original document' : version.source === 'ai_edit' ? 'AI edited version' : `${version.source} version`;

  return (
    <aside style={VERSION_RAIL}>
      <div style={VERSION_RAIL_HEADER}>
        <span style={VERSION_RAIL_TITLE}>Document version</span>
        <span style={VERSION_RAIL_COUNT}>
          {versions.length} {versions.length === 1 ? 'version' : 'versions'}
        </span>
      </div>
      {active && (
        <div style={VERSION_RAIL_ACTIVE_SUMMARY} title={active.fileName}>
          <span style={VERSION_RAIL_ACTIVE_LABEL}>{versionLabel(active)}</span>
          <span style={VERSION_RAIL_ACTIVE_TEXT}>{sourceLabel(active)}</span>
        </div>
      )}
      <div style={VERSION_RAIL_LIST}>
        {versions.map((version) => {
          const isActive = version.id === selectedVersionId;
          return (
            <button
              key={version.id}
              type="button"
              onClick={() => onSelect(version.id)}
              style={{
                ...VERSION_BTN,
                ...(isActive ? VERSION_BTN_ACTIVE : {}),
              }}
              title={version.fileName}
            >
              <span style={VERSION_BTN_MARKER}>{versionLabel(version)}</span>
              <span style={VERSION_BTN_BODY}>
                <span style={VERSION_BTN_NAME}>{version.fileName}</span>
                <span style={VERSION_BTN_META}>
                  {sourceLabel(version)}
                  {isActive ? ' · Selected' : ''}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </aside>
  );
}

function BaselineDisclosure({
  baseline,
  overridden,
}: {
  baseline: CapturedPlaceholderValue[];
  overridden: Map<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const remaining = baseline.filter((b) => !overridden.has(b.fieldId));
  if (remaining.length === 0) return null;
  return (
    <div style={{ marginTop: 4 }}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={DISCLOSURE_BTN}>
        {open ? 'Hide' : 'Show'} baseline ({remaining.length})
      </button>
      {open && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {remaining.map((b) => (
            <li key={b.fieldId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontFamily: APP.fontFamily, color: APP.textMuted }}>
              <span>{b.label ?? b.role ?? b.fieldId}</span>
              <span style={{ color: APP.textPrimary }}>{b.value}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Run progress view (post-submit) ───────────────────────────────────────

function RunProgressView({
  status,
  errorMessage,
  stageStatuses,
  onClose,
  onRunAgain,
  onGoBack,
  saveAsMode,
  saveAsWorkflowName,
  saveAsDefaultName,
  savePromptDismissed,
  onDismissSavePrompt,
  onSaveAsWorkflow,
}: {
  status?: WorkflowRunStatus;
  errorMessage?: string;
  stageStatuses: Record<ProgressStage['id'], 'pending' | 'running' | 'completed' | 'failed'>;
  onClose?: () => void;
  onRunAgain?: () => void;
  onGoBack?: () => void;
  saveAsMode?: SaveAsWorkflowMode;
  saveAsWorkflowName?: string;
  saveAsDefaultName?: string;
  savePromptDismissed?: boolean;
  onDismissSavePrompt?: () => void;
  onSaveAsWorkflow?: (name: string) => void | Promise<void>;
}) {
  const isTerminal = status === 'completed' || status === 'failed' || status === 'canceled';
  // Show the save prompt only on a successful send, when the parent has
  // wired the callbacks, and only if the user hasn't dismissed it yet.
  // Hidden entirely on failure/cancel — saving a failed run as a workflow
  // would carry the error forward, which is the opposite of what the
  // user wants.
  const showSavePrompt = status === 'completed'
    && !!saveAsMode
    && !!onSaveAsWorkflow
    && !savePromptDismissed;

  return (
    <section style={CARD}>
      <header style={CARD_HEADER}>
        <span style={CARD_TITLE}>
          {status === 'completed' ? 'Sent ✓' : status === 'failed' ? 'Send failed' : 'Sending…'}
        </span>
        <span style={CARD_HINT}>
          {status === 'completed'
            ? 'Recipient will receive the email shortly.'
            : status === 'failed'
              ? errorMessage ?? 'Something went wrong.'
              : 'Running clone → fill → sign → dispatch.'}
        </span>
      </header>
      <ol style={STAGE_STRIP}>
        {STAGES.map((stage, idx) => {
          const s = stageStatuses[stage.id] ?? 'pending';
          return (
            <li key={stage.id} style={STAGE_ROW}>
              <StageDot status={s} />
              <span style={STAGE_LABEL}>{stage.label}</span>
              {idx < STAGES.length - 1 && (
                <div style={{ ...STAGE_CONNECTOR, backgroundColor: s === 'completed' ? '#1B7A4B' : '#E8DED4' }} />
              )}
            </li>
          );
        })}
      </ol>

      {showSavePrompt && saveAsMode && (
        <SaveAsWorkflowPrompt
          mode={saveAsMode}
          workflowName={saveAsWorkflowName}
          defaultName={saveAsDefaultName ?? ''}
          onSave={onSaveAsWorkflow!}
          onDismiss={onDismissSavePrompt ?? (() => {})}
        />
      )}

      {isTerminal && (
        <div style={ACTIONS}>
          {status === 'failed' && onGoBack && (
            <button type="button" onClick={onGoBack} style={PRIMARY_BTN}>
              Go back
            </button>
          )}
          {onRunAgain && (
            <button type="button" onClick={onRunAgain} style={status === 'failed' && onGoBack ? SECONDARY_BTN : PRIMARY_BTN}>
              Run again
            </button>
          )}
          {onClose && (
            <button type="button" onClick={onClose} style={SECONDARY_BTN}>
              Close
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function StageDot({ status }: { status: 'pending' | 'running' | 'completed' | 'failed' }) {
  const colour = status === 'completed' ? '#1B7A4B'
    : status === 'failed' ? '#C84A2D'
      : status === 'running' ? APP.accentWorkflowPrimary
        : '#D9CFC4';
  return (
    <div
      aria-label={`Stage status: ${status}`}
      style={{
        width: 10,
        height: 10,
        borderRadius: 999,
        backgroundColor: colour,
        boxShadow: status === 'running' ? `0 0 0 4px ${APP.accentWorkflowPrimary}33` : 'none',
        flexShrink: 0,
        transition: 'all 180ms ease',
      }}
    />
  );
}

// ─── Small reusable bits ───────────────────────────────────────────────────

function FieldRow({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 11, color: APP.textMuted, fontFamily: APP.fontFamily, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
        {required && <span style={{ color: APP.accentWorkflowPrimary, marginLeft: 4 }}>*</span>}
      </span>
      {children}
    </div>
  );
}

function SectionHeader({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 11, color: APP.textMuted, fontFamily: APP.fontFamily, textTransform: 'uppercase', letterSpacing: 0.5, padding: '6px 4px 2px' }}>
      {children}
    </div>
  );
}

function PickRow({
  icon,
  title,
  subtitle,
  meta,
  selected,
  onClick,
  disabled,
  badge,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  meta?: string;
  selected?: boolean;
  onClick: () => void;
  disabled?: boolean;
  badge?: { label: string; isContracts: boolean };
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        border: `1px solid ${selected ? APP.accentWorkflowPrimary : APP.border}`,
        backgroundColor: selected ? 'color-mix(in srgb, #D97645 18%, var(--app-bg-version-card))' : APP.bgContent,
        borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        textAlign: 'left',
        fontFamily: APP.fontFamily,
      }}
    >
      <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: APP.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
            {title}
          </span>
          {badge && (
            <span style={{
              fontSize: 9,
              fontWeight: 600,
              lineHeight: '14px',
              padding: '1px 6px',
              borderRadius: 4,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              backgroundColor: badge.isContracts ? 'color-mix(in srgb, #D97645 18%, var(--app-bg-version-card))' : APP.bgSidebar,
              color: badge.isContracts ? APP.accentWorkflowPrimary : APP.textMuted,
              border: `1px solid ${badge.isContracts ? APP.accentWorkflowPrimary + '30' : APP.border}`,
            }}>
              {badge.isContracts ? '★ ' : ''}{!badge.isContracts && <AppFolderIcon width={13} height={10} style={{ verticalAlign: '-1px', marginRight: 4 }} />}{badge.label}
            </span>
          )}
        </div>
        {subtitle && (
          <div style={{ fontSize: 11, color: APP.textMuted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {subtitle}
          </div>
        )}
      </div>
      {meta && <span style={{ fontSize: 11, color: APP.textMuted, flexShrink: 0 }}>{meta}</span>}
      {selected && (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
          <path d="M3 7.5l3 3 5-6" stroke={APP.accentWorkflowPrimary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}

function DeltaPreviewRow({ label, value, locked }: { label: string; value: string; locked?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px dashed ${APP.border}` }}>
      <span style={{ fontSize: 12, color: APP.textMuted, fontFamily: APP.fontFamily }}>{label}</span>
      <span style={{ fontSize: 13, color: APP.textPrimary, fontFamily: APP.fontFamily, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
        {value}
        {locked && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2.5 5h5v3.5h-5V5zm.75 0V3.5a1.75 1.75 0 013.5 0V5" stroke={APP.textMuted} strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
    </div>
  );
}

// ─── Flow Steps Panel (right expandable) ────────────────────────────────────

const NODE_TYPE_META: Record<string, { emoji: string; label: string }> = {
  clone_document: { emoji: '📄', label: 'Clone document' },
  fill_placeholders: { emoji: '✏️', label: 'Fill placeholders' },
  apply_signature: { emoji: '🖊️', label: 'Apply signature' },
  dispatch_for_signature: { emoji: '📨', label: 'Dispatch for signature' },
  document_input: { emoji: '📥', label: 'Document input' },
  classify_document: { emoji: '🏷️', label: 'Classify document' },
  extract_fields: { emoji: '📋', label: 'Extract fields' },
  review_analysis: { emoji: '🔍', label: 'Review analysis' },
  draft_output: { emoji: '📝', label: 'Draft output' },
  export_result: { emoji: '📤', label: 'Export result' },
};

interface FlowStepsPanelProps {
  definition: WorkflowDefinition;
  /** When false, drag handles + delete buttons are hidden. */
  canEdit?: boolean;
  onReorderSteps?: (fromIndex: number, toIndex: number) => void;
  onDeleteStep?: (nodeId: string) => void;
}

function FlowStepsPanel({ definition, canEdit = false, onReorderSteps, onDeleteStep }: FlowStepsPanelProps) {
  const nodes = definition.graph.nodes;
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const entryNodeId = definition.graph.entryNodeId;

  return (
    <div style={FLOW_PANEL}>
      <div style={FLOW_PANEL_HEADER}>
        <span style={{ fontSize: 11, fontWeight: 700, color: APP.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.05em', fontFamily: APP.fontFamily }}>
          Flow steps
        </span>
        <span style={{ fontSize: 11, color: APP.textMuted, fontFamily: APP.fontFamily }}>
          {nodes.length} steps
        </span>
      </div>

      <div style={{ padding: '8px 0', flex: 1, overflowY: 'auto' }}>
        {nodes.map((node, idx) => {
          const meta = NODE_TYPE_META[node.type] ?? { emoji: '⚙️', label: node.type };
          const isOver = overIdx === idx && dragIdx !== idx;
          const isEntry = node.nodeId === entryNodeId;
          // Entry step is structurally required — never deletable. Reorder
          // is fine, but the new entry takes over as soon as the order
          // changes (handled in reorderSteps).
          const showDelete = canEdit && !isEntry && Boolean(onDeleteStep);
          return (
            <div key={node.nodeId}>
              <div
                draggable={canEdit}
                onDragStart={() => { if (canEdit) setDragIdx(idx); }}
                onDragOver={(e) => { if (!canEdit) return; e.preventDefault(); setOverIdx(idx); }}
                onDrop={(e) => {
                  if (!canEdit) return;
                  e.preventDefault();
                  const from = dragIdx;
                  setDragIdx(null);
                  setOverIdx(null);
                  if (from === null || from === idx) return;
                  onReorderSteps?.(from, idx);
                }}
                onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 16px',
                  cursor: canEdit ? 'grab' : 'default',
                  borderTop: isOver ? `2px solid ${APP.accentWorkflowPrimary}` : '2px solid transparent',
                  backgroundColor: dragIdx === idx ? 'var(--app-bg-search)' : 'transparent',
                  transition: 'background-color 120ms ease',
                }}
              >
                <span style={{ fontSize: 14, flexShrink: 0, width: 20, textAlign: 'center' }}>{meta.emoji}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: APP.textPrimary, fontFamily: APP.fontFamily, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {node.label || meta.label}
                  </div>
                  <div style={{ fontSize: 11, color: APP.textMuted, fontFamily: APP.fontFamily }}>
                    {meta.label}
                  </div>
                </div>
                {showDelete && (
                  <button
                    type="button"
                    aria-label={`Delete ${node.label || meta.label} step`}
                    title="Delete step"
                    onClick={(e) => {
                      e.stopPropagation();
                      const label = node.label || meta.label;
                      if (!window.confirm(`Delete the "${label}" step? This change saves to the workflow.`)) return;
                      onDeleteStep?.(node.nodeId);
                    }}
                    style={DELETE_ROW_BUTTON}
                  >
                    ×
                  </button>
                )}
                {canEdit && (
                  <svg width="10" height="14" viewBox="0 0 10 14" fill="none" style={{ flexShrink: 0, opacity: 0.3 }}>
                    <circle cx="3" cy="3" r="1.2" fill="#6B5E52" />
                    <circle cx="7" cy="3" r="1.2" fill="#6B5E52" />
                    <circle cx="3" cy="7" r="1.2" fill="#6B5E52" />
                    <circle cx="7" cy="7" r="1.2" fill="#6B5E52" />
                    <circle cx="3" cy="11" r="1.2" fill="#6B5E52" />
                    <circle cx="7" cy="11" r="1.2" fill="#6B5E52" />
                  </svg>
                )}
              </div>
              {idx < nodes.length - 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2px 0' }}>
                  <div style={{ width: 1, height: 16, backgroundColor: APP.bgWorkflowConnector }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

const CONTAINER: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: APP.bgMain,
  fontFamily: APP.fontFamily,
};

/** Step 1 — centered modal over the workflow overlay (matches Send-for-signature modal feel). */
const RECIPIENTS_MODAL_BACKDROP: CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  backgroundColor: 'rgba(0, 0, 0, 0.45)',
  boxSizing: 'border-box',
};

const RECIPIENTS_MODAL_CARD: CSSProperties = {
  width: 'min(520px, calc(100vw - 32px))',
  maxHeight: 'calc(100vh - 48px)',
  overflow: 'auto',
  borderRadius: 16,
  backgroundColor: APP.bgCardBody,
  border: `1px solid ${APP.border}`,
  boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
  fontFamily: APP.fontFamily,
};

const RECIPIENTS_MODAL_HEADER_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  padding: '18px 20px 12px',
  borderBottom: `1px solid ${APP.border}`,
};

const RECIPIENTS_MODAL_TITLE: CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: APP.textPrimary,
  fontFamily: APP.fontFamily,
  lineHeight: '22px',
};

const RECIPIENTS_MODAL_SUB: CSSProperties = {
  fontSize: 11,
  color: APP.textMuted,
  fontFamily: APP.fontFamily,
  marginTop: 2,
};

const MODAL_CLOSE_BTN: CSSProperties = {
  flexShrink: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: APP.textMuted,
  borderRadius: 8,
};

const TOP_BAR: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 24px',
  borderBottom: `1px solid ${APP.border}`,
  backgroundColor: 'var(--app-bg-content)',
};

const CRUMB_BTN: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 8px',
  borderRadius: 6,
  border: `1px solid ${APP.border}`,
  background: 'transparent',
  cursor: 'pointer',
};

const BODY_ROW: CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  overflow: 'hidden',
};

const MAIN: CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '24px 32px',
  overflow: 'auto',
};

const FLOW_PANEL: CSSProperties = {
  width: 260,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  borderLeft: `1px solid ${APP.border}`,
  backgroundColor: 'var(--app-bg-content)',
  overflow: 'hidden',
};

const FLOW_PANEL_HEADER: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 16px 10px',
  borderBottom: `1px solid ${APP.border}`,
};

const DELETE_ROW_BUTTON: CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '2px 8px',
  borderRadius: 4,
  color: APP.textMuted,
  fontSize: 18,
  lineHeight: 1,
  flexShrink: 0,
};

const CARD: CSSProperties = {
  width: '100%',
  maxWidth: 1100,
  backgroundColor: 'var(--app-bg-card-body)',
  border: `1px solid ${APP.border}`,
  borderRadius: 14,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const CARD_HEADER: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '20px 24px 8px',
};

const CARD_TITLE: CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  color: APP.textPrimary,
  fontFamily: APP.fontFamily,
};

const CARD_HINT: CSSProperties = {
  fontSize: 12,
  color: APP.textMuted,
  fontFamily: APP.fontFamily,
};

const FIELDS: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  padding: '12px 24px 16px',
};

const READONLY_PILL: CSSProperties = {
  fontSize: 13,
  color: APP.textMuted,
  fontFamily: APP.fontFamily,
  padding: '8px 12px',
  borderRadius: 8,
  backgroundColor: 'var(--app-bg-search)',
  border: `1px dashed ${APP.border}`,
  width: 'fit-content',
};

const INPUT: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 13,
  color: APP.textPrimary,
  fontFamily: APP.fontFamily,
  border: `1px solid ${APP.border}`,
  borderRadius: 8,
  backgroundColor: 'var(--app-bg-content)',
  outline: 'none',
  boxSizing: 'border-box',
};

const SUGGEST_DROPDOWN: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: 4,
  zIndex: 20,
  backgroundColor: 'var(--app-bg-content)',
  border: `1px solid ${APP.border}`,
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
  maxHeight: 220,
  overflowY: 'auto',
};

const SUGGEST_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '8px 12px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  fontSize: 12,
  color: APP.textPrimary,
  fontFamily: APP.fontFamily,
};

const CHIP: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 8px',
  borderRadius: 999,
  backgroundColor: APP.bgQuickAction,
  fontSize: 11,
  color: APP.textPrimary,
  fontFamily: APP.fontFamily,
};

const CHIP_REMOVE: CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  color: APP.textMuted,
  padding: 0,
  lineHeight: 1,
};

const ACTIONS: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 24px 20px',
};

const PRIMARY_BTN: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
  backgroundColor: APP.accentWorkflowPrimary,
  color: '#FFFFFF',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: APP.fontFamily,
};

const PRIMARY_BTN_DISABLED: CSSProperties = {
  ...PRIMARY_BTN,
  cursor: 'not-allowed',
  opacity: 0.45,
};

const PRIMARY_BTN_LARGE: CSSProperties = {
  ...PRIMARY_BTN,
  fontSize: 14,
  padding: '10px 22px',
};

const PRIMARY_BTN_DISABLED_LARGE: CSSProperties = {
  ...PRIMARY_BTN_LARGE,
  cursor: 'not-allowed',
  opacity: 0.45,
};

const SECONDARY_BTN: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  borderRadius: 8,
  border: `1px solid ${APP.border}`,
  cursor: 'pointer',
  backgroundColor: 'transparent',
  color: APP.textPrimary,
  fontSize: 13,
  fontWeight: 500,
  fontFamily: APP.fontFamily,
};

const MISSING_HINT: CSSProperties = {
  fontSize: 11,
  color: APP.textMuted,
  fontFamily: APP.fontFamily,
};

const TEMPLATES_SECTION: CSSProperties = {
  margin: '6px 24px 14px',
  padding: '12px 14px 8px',
  borderRadius: 12,
  // Soft warm tint so the section stands apart from neutral tab content
  // without screaming. The icon does the rest.
  border: `1px solid ${APP.accentWorkflowPrimary}33`,
  backgroundColor: `${APP.accentWorkflowPrimary}08`,
};

const TEMPLATES_HEADER: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: '0 4px 8px',
};

const TEMPLATES_HEADER_LABEL: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: APP.accentWorkflowPrimary,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontFamily: APP.fontFamily,
};

const TEMPLATES_HEADER_SUB: CSSProperties = {
  fontSize: 11,
  color: APP.textMuted,
  fontFamily: APP.fontFamily,
};

const TAB_BAR: CSSProperties = {
  display: 'flex',
  gap: 8,
  padding: '8px 24px 4px',
};

const TAB_BTN: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  borderRadius: 999,
  border: `1px solid ${APP.border}`,
  backgroundColor: 'transparent',
  fontSize: 12,
  color: APP.textMuted,
  fontFamily: APP.fontFamily,
  cursor: 'pointer',
};

const TAB_BTN_ACTIVE: CSSProperties = {
  ...TAB_BTN,
  backgroundColor: APP.accentWorkflowPrimary,
  color: '#FFFFFF',
  border: `1px solid ${APP.accentWorkflowPrimary}`,
  fontWeight: 600,
};

const DEFAULT_PILL: CSSProperties = {
  alignSelf: 'flex-start',
  margin: '0 24px 8px',
  padding: '6px 10px',
  border: `1px dashed ${APP.accentWorkflowPrimary}`,
  borderRadius: 999,
  background: 'transparent',
  color: APP.accentWorkflowPrimary,
  fontSize: 11,
  fontFamily: APP.fontFamily,
  cursor: 'pointer',
};

const LIST: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '8px 24px 16px',
  maxHeight: 480,
  overflowY: 'auto',
};

const EMPTY_HINT: CSSProperties = {
  fontSize: 12,
  color: APP.textMuted,
  fontFamily: APP.fontFamily,
  padding: 16,
  textAlign: 'center',
};

const EMPTY_BOX: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  padding: 24,
  border: `1px dashed ${APP.border}`,
  borderRadius: 10,
  textAlign: 'center',
  alignItems: 'center',
};

const SPLIT: CSSProperties = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  borderTop: `1px solid ${APP.border}`,
};

const PDF_PANE: CSSProperties = {
  flexGrow: 4,
  flexShrink: 1,
  flexBasis: 0,
  minHeight: 0,
  minWidth: 0,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  borderRight: `1px solid ${APP.border}`,
  backgroundColor: 'var(--app-bg-search)',
  overflow: 'hidden',
};

const VERSIONED_PREVIEW: CSSProperties = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  position: 'relative',
  display: 'flex',
};

const VERSION_RAIL: CSSProperties = {
  width: 176,
  flexShrink: 0,
  borderRight: `1px solid ${APP.border}`,
  backgroundColor: 'color-mix(in srgb, var(--app-bg-version-canvas) 96%, var(--app-bg-content))',
  padding: '12px 10px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  overflowY: 'auto',
};

const VERSION_RAIL_HEADER: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
};

const VERSION_RAIL_TITLE: CSSProperties = {
  fontSize: 12,
  fontWeight: 750,
  color: APP.textPrimary,
  fontFamily: APP.fontFamily,
};

const VERSION_RAIL_COUNT: CSSProperties = {
  fontSize: 11,
  color: APP.textMuted,
  fontFamily: APP.fontFamily,
};

const VERSION_RAIL_ACTIVE_SUMMARY: CSSProperties = {
  border: `1px solid color-mix(in srgb, ${APP.accentWorkflowPrimary} 32%, ${APP.border})`,
  borderRadius: 8,
  backgroundColor: 'color-mix(in srgb, #D97645 10%, var(--app-bg-version-card))',
  padding: '8px 9px',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const VERSION_RAIL_ACTIVE_LABEL: CSSProperties = {
  fontSize: 13,
  fontWeight: 800,
  color: APP.accentWorkflowPrimary,
  fontFamily: APP.fontFamily,
};

const VERSION_RAIL_ACTIVE_TEXT: CSSProperties = {
  fontSize: 11,
  lineHeight: 1.25,
  color: APP.textMuted,
  fontFamily: APP.fontFamily,
};

const VERSION_RAIL_LIST: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const VERSION_BTN: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  border: `1px solid ${APP.border}`,
  borderRadius: 8,
  backgroundColor: APP.bgVersionCard,
  color: APP.textPrimary,
  padding: '8px',
  cursor: 'pointer',
  fontFamily: APP.fontFamily,
  textAlign: 'left',
  width: '100%',
  minHeight: 50,
};

const VERSION_BTN_ACTIVE: CSSProperties = {
  borderColor: APP.accentWorkflowPrimary,
  backgroundColor: 'color-mix(in srgb, #D97645 12%, var(--app-bg-version-card))',
  color: APP.accentWorkflowPrimary,
  boxShadow: `inset 3px 0 0 ${APP.accentWorkflowPrimary}`,
};

const VERSION_BTN_MARKER: CSSProperties = {
  width: 32,
  height: 26,
  borderRadius: 6,
  backgroundColor: 'color-mix(in srgb, var(--app-text-primary) 8%, transparent)',
  color: APP.textPrimary,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontSize: 12,
  fontWeight: 800,
  fontFamily: APP.fontFamily,
};

const VERSION_BTN_BODY: CSSProperties = {
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const VERSION_BTN_NAME: CSSProperties = {
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 11,
  fontWeight: 650,
  color: APP.textPrimary,
};

const VERSION_BTN_META: CSSProperties = {
  fontSize: 10,
  lineHeight: 1.2,
  color: APP.textMuted,
  whiteSpace: 'normal',
};

const DELTAS_PANE: CSSProperties = {
  flexGrow: 1,
  flexShrink: 0,
  flexBasis: 380,
  minHeight: 0,
  padding: 20,
  overflowY: 'auto',
  minWidth: 300,
};

const DELTA_GROUP_LABEL: CSSProperties = {
  display: 'block',
  fontSize: 10,
  fontWeight: 700,
  color: APP.textMuted,
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  marginBottom: 6,
  fontFamily: APP.fontFamily,
};

const AI_EDIT_BOX: CSSProperties = {
  marginTop: 12,
  padding: 12,
  borderRadius: 8,
  border: `1px solid ${APP.border}`,
  backgroundColor: APP.bgCardBody,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const AI_EDIT_REVIEW_BOX: CSSProperties = {
  marginTop: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const AI_EDIT_REVIEW_META: CSSProperties = {
  fontSize: 10,
  color: APP.textMuted,
  fontFamily: APP.fontFamily,
  lineHeight: '12px',
  whiteSpace: 'nowrap',
};

const AI_EDIT_REVIEW_HINT: CSSProperties = {
  margin: 0,
  fontSize: 11,
  color: APP.textSecondary,
  lineHeight: '15px',
  fontFamily: APP.fontFamily,
};

const AI_EDIT_TEXTAREA: CSSProperties = {
  ...INPUT,
  minHeight: 72,
  resize: 'vertical',
  lineHeight: '18px',
};

const DELTA_EDIT_ROW: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '6px 0',
};

const DISCLOSURE_BTN: CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: APP.accentWorkflowPrimary,
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  padding: 0,
  fontFamily: APP.fontFamily,
};

const STAGE_STRIP: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  listStyle: 'none',
  padding: '0 24px 16px',
  margin: 0,
  gap: 0,
};

const STAGE_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const STAGE_LABEL: CSSProperties = {
  fontSize: 12,
  color: APP.textPrimary,
  fontFamily: APP.fontFamily,
};

const STAGE_CONNECTOR: CSSProperties = {
  width: 36,
  height: 2,
  margin: '0 12px',
};
